import React, { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { useTranslation } from 'react-i18next';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./App.css";
import "./styles/unified-calendar.css";
import { showErrorToast, showToast as showSharedToast } from './utils/toast';   // Block 233; fix(toast)
import { messageFromError } from './utils/apiError';   // Block 233
import axios from "axios";  // Import axios for API calls
import TaskList from "./components/TaskList";
import Notes from "./components/Notes";
import CalendarView from "./components/CalendarView";
import NotificationsPage from "./components/NotificationsPage"; // Block 208 + the 2026-09-22 merge: one page for everything waiting
import WeekView from "./components/WeekView";
import { formatNoteDate, combineDateTime, parseLocalDate } from "./utils/dateUtils";
import { findNextAvailableSlot, sortTasks, sortByTimeOnly, tasksForDate, suggestPrioritySwap, findNextAvailableSlotMultiDay } from "./utils/taskUtils";
import { getRecurringTasksForDateRange } from "./utils/recurrenceUtils";
import { toLocalDateStr } from "./utils/calendarUtils";
import { priorityRank, taskTypes, personalTaskTypes, validViewOptions } from "./utils/constants";
import Dashboard from "./components/Dashboard";
import TasksPage from "./components/TasksPage";
import ProjectTracker from "./components/ProjectTracker";
import projectManager from "./models/projects";
import { AdminPanelRoute } from "./components/AdminPanel";
import Login from "./components/Login";
import Register from "./components/Register";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import VerifyEmail from "./components/VerifyEmail";
import VerifyEmailChange from "./components/VerifyEmailChange";   // decision 10a
import RevertEmailChange from "./components/RevertEmailChange";   // item 21 part 2
import TaskModal from "./components/modals/TaskModal";
import ViewNoteModal from "./components/modals/ViewNoteModal";
import SuperAideyChat from "./components/SuperAideyChat";
import MeetingRecorder from "./components/MeetingRecorder";
import AideyChatDashboard from "./components/AideyChat/AideyChatDashboard";
import AideyPopup from "./components/AideyChat/AideyPopup";   // Block C
import { newSessionId } from "./services/aideySessionsService";
import Profile from "./components/Profile";
import EmailInbox from "./components/Email/EmailInbox";
import ZoomCallback from "./components/ZoomCallback";
import AcceptInvite from "./components/AcceptInvite";
import TeamManagement from "./components/TeamManagement";
// Not this page's stylesheet any more (UI-17 has TeamCalendar.css), but its class names are still
// used by Dashboard, TeamManagement, EmailActions and GoogleCalendarEventsViewer, which got these
// rules only because TeamCalendar imported it. Kept global, at the same place in the cascade, so they do not change.
import "./components/TeamOverview.css";
import TeamCalendar from "./components/TeamCalendar";
import TeamDashboard from "./components/TeamDashboard";
import TaskDueHistory from "./components/task/TaskDueHistory";
import ContactsDashboard from "./components/ContactsDashboard";
import CRMDashboard from "./components/CRM/CRMDashboard";
import InvoicingLayout from "./components/Invoicing/InvoicingLayout";
import FinancialDashboard from "./components/Financials/FinancialDashboard";
import BasicFinancialSummary from "./components/Financials/BasicFinancialSummary";
import PlanSelectPage from "./components/PlanSelectPage";
import TrialPaywall from "./components/TrialPaywall";
import CoveredMemberPaused from "./components/CoveredMemberPaused";
import ChildPaused from "./components/ChildPaused";
import SoloProOffer from "./components/SoloProOffer";
import PlanGate from "./components/PlanGate";
import { featureAllowed } from "./utils/planRule";   // UI-24 item 19: invoicing is a Basics feature (not Personal or Family)
import SearchOverlay from "./components/SearchOverlay";
import { ICalProvider } from "./contexts/ICalContext";
import { CurrentUserProvider } from "./contexts/CurrentUserContext";
import { ContactsProvider } from "./contexts/ContactsContext";
import { routeNotifications } from "./utils/notificationDelivery"; // re-test 24: what may be cleared without her
import { InvoicesProvider } from "./contexts/InvoicesContext";
import { ExpensesProvider } from "./contexts/ExpensesContext";
import { useCurrentUser } from "./contexts/CurrentUserContext";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import usePushNotifications from "./hooks/usePushNotifications";
import { api, refreshAccessToken } from "./api/client";
import TrialBanner from "./components/TrialBanner";
import useGoogleCalendarEvents from "./hooks/useGoogleCalendarEvents";
import useICalEvents from "./hooks/useICalEventsNew";
import { useOutlookEvents } from "./hooks/useOutlookEvents";
import { redirectToCheckout } from './utils/stripe';
import RemovedMemberBanner from './components/RemovedMemberBanner'; // Tier 1 #10 (D-5)
import NotificationBell from './components/NotificationBell'; // Tier 1 #7: mounted at last
import { setDeepLink, entityFromUrl, useDeepLinkItem, readPendingDeepLink, clearPendingDeepLink } from './utils/deepLink'; // Block D20/D21/D23: notification item deep-link
import { VIEW_TO_PATH, viewForPath, NOT_FOUND, settingsSectionForPath } from './nav/paths';   // fresh-eyes item 27: an unknown address says so
import NotFoundPage from './components/NotFoundPage';
import { personalFlag, notificationView, sidebarViews, isAdminRole, sidebarGroups, sidebarFooter, navTarget, itemId } from './nav/views'; // UI-0b: every navigation lands on a page this workspace has
import { userCardRole } from './utils/roleLabel'; // decision 60: the user card's role
import { isTaskOverdue, isTaskToDo, formatTaskTimeRange, formatTaskDateLine, formatTaskClock, taskFormDates, taskFormError } from './utils/taskTime'; // Block D20 Part A: overdue in the user's STORED timezone; fix(task-time): the time shown
import DocumentImporter from './components/Projects/DocumentImporter';
import { TourProvider } from './tours/TourContext';
import ErrorBoundary from './components/shared/ErrorBoundary';
import ConfirmDeleteModal from './components/shared/ConfirmDeleteModal';
import WorkspaceSwitcher from './components/WorkspaceSwitcher';
// UI-4: the app shell (one Sidebar for desktop, tablet rail and phone drawer; the phone top bar).
import Sidebar from './ui/shell/Sidebar';
import TopBar from './ui/shell/TopBar';
import Drawer from './ui/shell/Drawer';
import { FloatingAsk } from './ui/FloatingAsk';
import { useBreakpoint } from './ui/breakpoints';
import { deliverUnasked, isPhoneViewport, queueChatIntro, takeQueuedChatIntro } from './utils/chatAutoOpen';   // decision 40
import { useCalendarDefaultTab } from './hooks/useCalendarDefaultTab';   // decision 9d: Settings > Calendar applies at once
import useChatTaskActions from './hooks/useChatTaskActions';
import useTaskCounts from './hooks/useTaskCounts';   // fresh-eyes 15: My tasks' two numbers, from the server   // decision 75: typed move/cancel in chat, with Undo
import ShoppingList from './components/ShoppingList';
import Renewals from './components/Renewals';
import Journal  from './components/Journal';
import Family       from './components/Family';
import FamilyChat   from './components/FamilyChat';
import MealPlanner  from './components/MealPlanner';
import FamilyCalendar from './components/FamilyCalendar';
import RecurringEditDialog from './components/RecurringEditDialog';   // decision 65: recurring edits
import { occurrenceOf, saveSeriesEdit, setOccurrenceState, skipOccurrence, applyOccurrenceLocally, repeatOffKeepsOccurrence } from './utils/recurringEdit';
import { setCalendarSyncErrors } from './utils/calendarSyncErrors';   // calendar-2
import CalendarSyncNotice from './components/task/CalendarSyncNotice';
import Budget from './components/Budget';
const SettingsLayout = React.lazy(() => import('./components/Settings/SettingsLayout'));
const TimesheetView = React.lazy(() => import('./components/TimeTracking/TimesheetView'));
const InsightsPage = React.lazy(() => import('./components/Insights/InsightsPage'));
const TaskTimePrompt = React.lazy(() => import('./components/TimeTracking/TaskTimePrompt'));
const FormsPage = React.lazy(() => import('./components/Forms/FormsPage'));
const PublicFormPage = React.lazy(() => import('./components/Forms/PublicFormPage'));
const WorkflowsDashboard = React.lazy(() => import('./components/Workflows/WorkflowsDashboard'));
// UI-2: the private UI kit, loaded only for an admin who opens /dev/ui-kit.
const UiKitPage = React.lazy(() => import('./ui/kit/UiKitPage'));
const ProposalsDashboard = React.lazy(() => import('./components/Proposals/ProposalsDashboard'));
const PublicProposalPage = React.lazy(() => import('./components/Proposals/PublicProposalPage'));
const SchedulerDashboard = React.lazy(() => import('./components/Scheduler/SchedulerDashboard'));
const PublicBookingPage = React.lazy(() => import('./components/Scheduler/PublicBookingPage'));
const LazyCancelPage = React.lazy(() =>
  import('./components/Scheduler/CancelReschedulePage').then(m => ({ default: m.CancelPage }))
);
const LazyReschedulePage = React.lazy(() =>
  import('./components/Scheduler/CancelReschedulePage').then(m => ({ default: m.ReschedulePage }))
);
const LazyRebookPage = React.lazy(() => import('./components/Scheduler/RebookPage')); // Block E1c
const PortalDashboard = React.lazy(() => import('./components/ClientPortal/PortalDashboard'));
const PublicPortalPage = React.lazy(() => import('./components/ClientPortal/PublicPortalPage'));
const ContractsDashboard = React.lazy(() => import('./components/Contracts/ContractsDashboard'));
const PublicContractPage = React.lazy(() => import('./components/Contracts/PublicContractPage'));
const WelcomePage = React.lazy(() => import('./components/Auth/WelcomePage'));
// Card at sign-up (phase 2): the "Finish starting your free trial" screen and Checkout's return page.
const StartTrialScreen = React.lazy(() => import('./components/StartTrial/StartTrialScreen'));
const CheckoutReturnPage = React.lazy(() => import('./components/StartTrial/CheckoutReturnPage'));
const HROverview = React.lazy(() => import('./components/Team/HROverview'));
const PublicInvoicePage = React.lazy(() => import('./components/Invoicing/PublicInvoicePage'));
const InvoiceLinkMovedPage = React.lazy(() => import('./components/Invoicing/InvoiceLinkMovedPage'));
const PublicPOPage = React.lazy(() => import('./components/PurchaseOrders/PublicPOPage'));
const PurchaseOrders = React.lazy(() => import('./components/PurchaseOrders/PurchaseOrders'));

// Wrapper component to handle Zoom callback routing
function AppWrapper() {
  const urlParams = new URLSearchParams(window.location.search);

  if (window.location.pathname === '/welcome') {
    return (
      <CurrentUserProvider>
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <WelcomePage />
        </React.Suspense>
      </CurrentUserProvider>
    );
  }
  if (window.location.pathname === '/zoom/callback') {
    return <ZoomCallback />;
  }
  if (window.location.pathname === '/register') {
    return (
      <CurrentUserProvider>
        <Register />
      </CurrentUserProvider>
    );
  }
  // Card at sign-up: Stripe Checkout's success_url. The page confirms the session on the server
  // (never trusting the address) and then opens the dashboard.
  if (window.location.pathname === '/start-trial/return') {
    return (
      <CurrentUserProvider>
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <CheckoutReturnPage />
        </React.Suspense>
      </CurrentUserProvider>
    );
  }
  if (window.location.pathname === '/plan-select') {
    return (
      <CurrentUserProvider>
        <PlanSelectPage />
      </CurrentUserProvider>
    );
  }
  if (window.location.pathname === '/forgot-password') {
    return <ForgotPassword />;
  }
  if (window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }
  if (window.location.pathname === '/verify-email') {
    return <VerifyEmail />;
  }
  if (window.location.pathname === '/verify-email-change') {
    return <VerifyEmailChange />;
  }
  if (window.location.pathname === '/revert-email-change') {
    return <RevertEmailChange />;
  }
  if (window.location.pathname === '/accept-invite') {
    return <AcceptInvite />;
  }
  if (window.location.pathname.startsWith('/forms/')) {
    const parts = window.location.pathname.replace('/forms/', '').split('/');
    const formUsername = parts[0];
    const formSlugPart = parts[1];
    if (formUsername && formSlugPart) {
      return (
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <PublicFormPage slug={formSlugPart} />
        </React.Suspense>
      );
    }
  }
  if (window.location.pathname.startsWith('/f/')) {
    const slug = window.location.pathname.replace('/f/', '');
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <PublicFormPage slug={slug} />
      </React.Suspense>
    );
  }
  // SITTING 2.1: the owner's preview of their own document, /preview/contract/:id
  // and /preview/proposal/:id. Authenticated (the page calls the owner-only
  // preview endpoint); a signed-out visitor gets nothing back and the page says
  // it could not be loaded. Deliberately NOT a public link.
  if (window.location.pathname.startsWith('/preview/')) {
    const [kind, previewId] = window.location.pathname.replace('/preview/', '').split('/');
    if (previewId && kind === 'contract') {
      return (
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <PublicContractPage previewId={previewId} />
        </React.Suspense>
      );
    }
    if (previewId && kind === 'proposal') {
      return (
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <PublicProposalPage previewId={previewId} />
        </React.Suspense>
      );
    }
  }
  if (window.location.pathname.startsWith('/proposals/')) {
    const tok = window.location.pathname.replace('/proposals/', '');
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <PublicProposalPage token={tok} />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/proposal/')) {
    const tok = window.location.pathname.replace('/proposal/', '');
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <PublicProposalPage token={tok} />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/book/')) {
    const parts = window.location.pathname.replace('/book/', '').split('/');
    const bookUsername = parts[0];
    const bookSlugPart = parts[1] || null;
    if (bookUsername) {
      return (
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <PublicBookingPage username={bookUsername} slug={bookSlugPart} />
        </React.Suspense>
      );
    }
  }
  if (window.location.pathname.startsWith('/cancel/')) {
    const cancelToken = window.location.pathname.replace('/cancel/', '');
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <LazyCancelPage token={cancelToken} />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/reschedule/')) {
    const rescheduleToken = window.location.pathname.replace('/reschedule/', '');
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <LazyReschedulePage token={rescheduleToken} />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/rebook/')) {
    const rebookToken = window.location.pathname.replace('/rebook/', '');
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <LazyRebookPage token={rebookToken} />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/portal/')) {
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <PublicPortalPage />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/contracts/')) {
    const contractToken = window.location.pathname.replace('/contracts/', '');
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <PublicContractPage token={contractToken} />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/contract/')) {
    const contractToken = window.location.pathname.replace('/contract/', '');
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <PublicContractPage token={contractToken} />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/invoice/')) {
    const invParts = window.location.pathname.replace('/invoice/', '').split('/');
    // Block 168: token-keyed public invoice — /invoice/t/{token}.
    if (invParts[0] === 't' && invParts[1]) {
      return (
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <PublicInvoicePage />
        </React.Suspense>
      );
    }
    // Block 168: the old /invoice/{username}/{number} links are dead (the surface was guessable).
    // Render the inert "link moved" page — no lookup, offers a resend, and catches in-flight Stripe
    // redirects that still point here.
    if (invParts[0] && invParts[1]) {
      return (
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <InvoiceLinkMovedPage />
        </React.Suspense>
      );
    }
  }
  if (window.location.pathname.startsWith('/po/')) {
    // Block H-5: /po/t/<token>; the page itself answers the old /po/<username>/<number> shape
    // with "not available", so any two-part address renders it.
    const poParts = window.location.pathname.replace('/po/', '').split('/');
    if (poParts[0] && poParts[1]) {
      return (
        <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
          <PublicPOPage />
        </React.Suspense>
      );
    }
  }
  return <App />;
}


// ── View ↔ URL path mapping: nav/paths.js (VIEW_TO_PATH, viewForPath; fresh-eyes item 27) ──

// Main App Content component that uses the CurrentUser context
function AppContent() {
  const { currentUser, loading: authLoading, logout, isPro: userIsPro, isTeam: userIsTeam, isTeamAdmin: userIsTeamAdmin, notifications, dismissNotifications, markNotificationAsRead, authConnectionFailed, retryConnection, workspacesLoadError, activeWorkspaceId, workspaces, subscriptionCheckPending } = useCurrentUser();
  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const isPersonalWorkspace = activeWorkspace?.type === 'personal';
  // The same fact as a three-state flag (null until workspaces load), read through a
  // ref by the notification deep-link handler below, whose effect does not re-run on
  // a workspace change.
  const workspaceIsPersonalRef = useRef(null);
  workspaceIsPersonalRef.current = personalFlag(workspaces, activeWorkspaceId);
  // Personal workspace gets its own task category list; business list unchanged.
  const activeTaskTypes = isPersonalWorkspace ? personalTaskTypes : taskTypes;

  // Single source of truth for personal-workspace nav. Both desktop sidebar and
  // mobile menu gate against this set — no duplicate lists.
  const PERSONAL_NAV_VIEWS = new Set([
    'Dashboard', 'Tasks', 'EmailDashboard', 'Notes', 'AideyChat',
    'Contacts', 'ProjectTracker', 'Shopping', 'Renewals', 'Birthdays', 'Journal', 'Family', 'FamilyChat', 'MealPlanner', 'Budget',
  ]);

  usePushNotifications(currentUser?.id);

  const { t } = useTranslation('common');
  const [tasks, setTasks] = useState([]);
  const [tasksLoadFailed, setTasksLoadFailed] = useState(false);   // a failed load must not read as "all clear"
  const [tasksLoaded, setTasksLoaded] = useState(false);           // item 8: a task link waits for this, then opens or says it is gone
  // Decision 75: the chat's typed move and cancel, each with Undo (hooks/useChatTaskActions).
  const chatTaskActions = useChatTaskActions({ tasks, setTasks });
  const [viewNote, setViewNote] = useState(null);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [editingId, setEditingId] = useState(null); // Single editingId state
  const [filter, setFilter] = useState(() => viewForPath(window.location.pathname));
  // The page the chat was opened from: the chat page's close (phone and tablet) returns there.
  const pageBeforeChat = useRef('Dashboard');
  useEffect(() => { if (filter !== 'AideyChat') pageBeforeChat.current = filter; }, [filter]);
  // Settings > Calendar > Default view; a saved change applies at once (decision 9d).
  const calInitialTab = useCalendarDefaultTab(currentUser?.id);
  // Block 208: Attention Required badge. One cheap indexed count
  // (event_proposals, workspace+status indexed) — no rows fetched. Refreshed on the
  // same aidey:tasksUpdated event the proposal banner already dispatches when a
  // proposal is accepted, resolved or dismissed, so the badge tracks the list.
  const [attentionCount, setAttentionCount] = useState(0);
  useEffect(() => {
    // Not before the session and the workspace are known (Carla, 2026-10-11): it fired under the
    // boot splash with neither, and again once they arrived.
    if (!currentUser?.id || !activeWorkspaceId) return undefined;
    let alive = true;
    const load = () => {
      api('/api/event-proposals/count')
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (alive && d && typeof d.count === 'number') setAttentionCount(d.count); })
        .catch(() => {});
    };
    load();
    window.addEventListener('aidey:tasksUpdated', load);
    return () => { alive = false; window.removeEventListener('aidey:tasksUpdated', load); };
  }, [activeWorkspaceId, currentUser?.id]);

  const [showModal, setShowModal] = useState(false);
  const [viewTask, setViewTask] = useState(null);
  const [taskDeleteConfirm, setTaskDeleteConfirm] = useState(null);
  const [viewTaskAttachments, setViewTaskAttachments] = useState([]);
  // Block D20: a task deep-link opens that task's detail modal (once the list is loaded).
  // Item 8 (2026-10-12): once the tasks have loaded it opens the task, or says "This item is no
  // longer available" (it waited for ever, silently, on a deleted task). useDeepLinkItem retires
  // the link (D23) only once it has been answered.
  useDeepLinkItem('task', {
    ready: tasksLoaded,
    find: (id) => (Array.isArray(tasks) ? tasks : []).find(x => String(x.id) === id),
    open: (t) => { setViewTask(t); setFilter('Tasks'); },
  });
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [deadlineDate, setDeadlineDate] = useState(null);
  const [deadlineTime, setDeadlineTime] = useState(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [sortBy, setSortBy] = useState("time");
  const [selectedWeekDate, setSelectedWeekDate] = useState(null);
  const [showWeekModal, setShowWeekModal] = useState(false);
  const [taskType, setTaskType] = useState("");
  const [assignedTeam, setAssignedTeam] = useState([]);
  const [householdMemberId, setHouseholdMemberId] = useState(null);
  const [reminderOffset, setReminderOffset] = useState(5);
  const [customReminder, setCustomReminder] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // UI-4: which shell is showing (phone top bar and drawer, tablet rail, desktop sidebar).
  const shellBp = useBreakpoint();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    localStorage.getItem('aidey_sidebar_collapsed') === 'true'
  );
  // UI-4: the bell panel opens beside the sidebar at its REAL width (it was a guessed 64/224),
  // and the drawer closes if the window grows past the phone and tablet widths.
  useEffect(() => {
    const side = shellBp === 'phone' ? '0px' : (shellBp === 'tablet' || sidebarCollapsed) ? 'var(--sidebar-rail)' : 'var(--sidebar-width)';
    document.documentElement.style.setProperty('--ui-shell-side', side);
  }, [shellBp, sidebarCollapsed]);
  useEffect(() => { if (shellBp === 'desktop') setIsMobileMenuOpen(false); }, [shellBp]);
  const [emailUnreadCount, setEmailUnreadCount] = useState(0);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const [quickNotes, setQuickNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [noteCategories, setNoteCategories] = useState([]);
  const [searchNote, setSearchNote] = useState("");
  const [productivityMode, setProductivityMode] = useState("Today");
  const [dashboardSortBy, setDashboardSortBy] = useState("time");
  const today = new Date();
  const [chatLoading, setChatLoading] = useState(false);
  const [showAideyChat, setShowAideyChat] = useState(false);
  const [showDocumentImporter, setShowDocumentImporter] = useState(false);
  // The file the chat's paperclip chose, handed to the importer already loaded (null otherwise).
  const [documentImporterFile, setDocumentImporterFile] = useState(null);
  const [isChatMinimised, setIsChatMinimised] = useState(() => localStorage.getItem('aidey-chat-minimised') === 'true');
  const [projectInvoiceTarget, setProjectInvoiceTarget] = useState(null);
  const [billTimeTarget, setBillTimeTarget] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [popupSessionId, setPopupSessionId] = useState(() => newSessionId());
  const popupCloseRef = useRef(null);
  const popupActiveSessionRef = useRef(null);   // Block C: the popup's live session, for Expand
  const escalatingRef = useRef(new Set()); // task IDs currently being escalated (prevents double-write)
  const [showTeamUpgradeModal, setShowTeamUpgradeModal] = useState(false);
  const [showTeamWelcomeModal, setShowTeamWelcomeModal] = useState(false);
  const [proactiveMessage, setProactiveMessage] = useState(null);
  const [notificationProactive, setNotificationProactive] = useState(null);
  const notifInitRef = useRef(false);
  const deliveredNotifIdsRef = useRef(new Set());
  const [prefillChatInput, setPrefillChatInput] = useState("");
  const [notifContext, setNotifContext] = useState(null);
  const promptedMeetingsRef = useRef(new Set());
  const [busy, setBusy] = useState(true);
  const [isPersonal, setIsPersonal] = useState(false);
  const [meetingType, setMeetingType] = useState(null);
  const [location, setLocation] = useState("");
  const [recurrence, setRecurrence] = useState(null);
  const [taskProject, setTaskProject] = useState(null);
  const [showClashModal, setShowClashModal] = useState(false);
  const [clashInfo, setClashInfo] = useState(null);
  const [taskFormClash, setTaskFormClash] = useState(null);
  // Decision 65: the occurrence of a repeating task being edited (null for any other task), and the
  // "which occurrences?" question waiting over the form ({ fromClash }).
  const [editingOccurrence, setEditingOccurrence] = useState(null);
  const [seriesPrompt, setSeriesPrompt] = useState(null);
  const [clashSlotConfirming, setClashSlotConfirming] = useState(false);
  const [clashContext, setClashContext] = useState(null);
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", content: "Hi! I'm Aidey. How can I help you?" }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [settingsInitialSection, setSettingsInitialSection] = useState(() => settingsSectionForPath(window.location.pathname));   // decision 10b: /pricing, /billing

  // Calendar event hooks — used for proactive meeting detection
  const { events: googleEvents } = useGoogleCalendarEvents();
  // FROM THE ACCOUNT, NOT THIS BROWSER (2026-10-04). This read the feed URL out of
  // localStorage, so a calendar subscribed on one device was silently absent on every
  // other — no error, because nothing failed. The localStorage copy is still consulted
  // as a fallback for a browser that has not refreshed its user yet, but the account
  // is the source of truth.
  const icalUrl = currentUser?.preferences?.icalUrl || (() => {
    try {
      const p = JSON.parse(localStorage.getItem("userProfile") || "{}");
      return p.integrations?.ical?.connected ? p.integrations.ical.url : null;
    } catch { return null; }
  })();
  const { events: icalEvents } = useICalEvents(icalUrl);
  const { events: outlookEvents } = useOutlookEvents();

  // Proactive meeting detection — runs every 60s, auto-opens Aidey chat
  useEffect(() => {
    const WINDOW_MS = 5 * 60 * 1000; // 5-minute lookahead

    function getEventStart(e) {
      if (e.datetime) return new Date(e.datetime);
      if (e.date && e.time) return new Date(`${e.date}T${e.time}`);
      if (e.start) return new Date(e.start);
      return null;
    }

    function isMeetingLike(e) {
      return /meeting|call|appointment|interview|standup|sync/i.test(
        (e.taskType || '') + ' ' + (e.title || '') + ' ' + (e.summary || '') + ' ' + (e.subject || '')
      );
    }

    function check() {
      const now = new Date();
      const allEvents = [
        ...(tasks || []).map(t => ({ id: String(t.id), title: t.title, start: getEventStart(t), isMeeting: isMeetingLike(t) })),
        ...(googleEvents || []).map(e => ({ id: e.id || e.title, title: e.title || e.summary, start: getEventStart(e), isMeeting: true })),
        ...(icalEvents || []).map(e => ({ id: e.id || e.title, title: e.title || e.summary, start: getEventStart(e), isMeeting: true })),
        ...(outlookEvents || []).map(e => ({ id: e.id || e.title, title: e.title || e.subject, start: getEventStart(e), isMeeting: true })),
      ].filter(e => e.start && e.isMeeting);

      const upcoming = allEvents.find(e => {
        if (promptedMeetingsRef.current.has(e.id)) return false;
        const diff = e.start - now;
        return diff >= 0 && diff <= WINDOW_MS;
      });

      if (upcoming) {
        promptedMeetingsRef.current.add(upcoming.id);
        const msgId = `${upcoming.id}-${Date.now()}`;
        const minsUntil = Math.max(1, Math.ceil((upcoming.start - now) / 60000));
        // Extract attendee names from the event if available
        const rawEvent = [
          ...(googleEvents || []),
          ...(icalEvents || []),
          ...(outlookEvents || []),
        ].find(e => (e.id || e.title || e.summary || e.subject) === upcoming.id || (e.title || e.summary || e.subject) === upcoming.title);
        const attendees = rawEvent?.attendees
          ? rawEvent.attendees.map(a => ({ name: a.displayName || a.name || a.email || a, email: a.email })).filter(a => a.name)
          : [];

        // Decision 40: on a phone this is a toast; its "Open" is the tap that opens the chat.
        deliverUnasked({
          phone: isPhoneViewport(),
          toastMessage: `Your meeting "${upcoming.title}" starts in ${minsUntil} minute${minsUntil === 1 ? '' : 's'}.`,
          open: () => {
            setProactiveMessage({ id: msgId, title: upcoming.title, attendees, minsUntil });
            setPopupSessionId(newSessionId());
            setShowAideyChat(true);
          },
        });
      }
    }

    check();
    const interval = setInterval(check, 60 * 1000);
    return () => clearInterval(interval);
  }, [tasks, googleEvents, icalEvents, outlookEvents]); // eslint-disable-line

  // Handle OAuth redirect back to app (?section=&connected=&error=)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    const connected = params.get('connected');
    const emailChanged = params.get('emailChanged');
    const error = params.get('error');

    if (section || connected || emailChanged || error) {
      if (section) setSettingsInitialSection(section);
      setFilter('Settings');

      if (connected) {
        showToast({ type: 'success', message: `${connected} connected successfully` });
      } else if (emailChanged) {
        showToast({ type: 'success', message: 'Email address updated successfully' });
      } else if (error) {
        const oauthErrorMessages = {
          microsoft_failed: 'Microsoft connection failed, please try again.',
          google_failed: 'Google connection failed, please try again.',
          zoom_failed: 'Zoom connection failed, please try again.',
          server_error: 'Connection failed due to a server error, please try again.',
        };
        showToast({ type: 'error', message: oauthErrorMessages[error] || decodeURIComponent(error) });
      }

      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Sync filter with browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const view = viewForPath(window.location.pathname);
      setFilter(view);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Set default meeting type from user preferences on component mount
  useEffect(() => {
    const userPrefs = getUserPreferences();
    if (userPrefs.defaultMeetingType && userPrefs.defaultMeetingType !== "") {
      setMeetingType(userPrefs.defaultMeetingType);
    }
  }, []);

  // Refresh tasks when drag & drop reschedules via custom event
  useEffect(() => {
    async function onTasksUpdated() {
      try {
        const res = await api('/api/tasks');
        const data = await res.json();
        setCalendarSyncErrors(data.calendarSyncErrors);   // calendar-2: left out = not read, kept
        // A task the chat just cancelled stays gone while its Undo is open (decision 75).
        if (Array.isArray(data.tasks)) { setTasks(chatTaskActions.withoutPending(data.tasks)); setTasksLoadFailed(false); setTasksLoaded(true); }
      } catch {
        setTasksLoadFailed(true);
      }
    }
    window.addEventListener("aidey:tasksUpdated", onTasksUpdated);
    return () => window.removeEventListener("aidey:tasksUpdated", onTasksUpdated);
  }, []);

  // Cmd+K / Ctrl+K global search shortcut
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(s => !s);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Plan access — derived from context helpers (trial-aware)
  const hasProPlanAccess = userIsPro;
  const hasTeamPlanAccess = userIsTeam;
  // UI-24 item 19: invoicing is in Basics (10 sent a month; lib/planGates invoicing tier basics). The
  // feature, not the bare 'basics' rank: Personal and Family rank with Basics and have no invoicing
  // (decision 4, utils/planRule CONSUMER_PLANS_INCLUDE_INVOICING). Basics gets the Invoices tab only.
  const hasBasicsPlanAccess = featureAllowed(currentUser, 'invoicing');

  // Handle team nav clicks — show upgrade prompt for non-team; TeamHR is admin-only, Dashboard/Calendar open to all members
  const handleTeamNavClick = (page) => {
    if (!hasTeamPlanAccess) {
      setShowTeamUpgradeModal(true);
    } else if ((page === 'TeamHR' || page === 'TeamDashboard') && !userIsTeamAdmin) {
      // TeamHR + TeamDashboard are admin-only — nav items hidden for members; safety net
    } else {
      handleMenuItemClick(page);
    }
  };

  // Show Team Pro welcome modal on first login after upgrade
  useEffect(() => {
    if (!currentUser) return;
    const plan = (currentUser.plan || '').toLowerCase();
    if (plan === 'team' && currentUser.preferences?.firstTeamLogin) {
      setShowTeamWelcomeModal(true);
    }
  }, [currentUser?.id, currentUser?.preferences?.firstTeamLogin]);

  const dismissTeamWelcome = async () => {
    setShowTeamWelcomeModal(false);
    try {
      await api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify({ preferences: { firstTeamLogin: false } }),
      });
    } catch (err) { showToast({ type: 'error', message: messageFromError(err, "Could not dismiss that.") }); }
  };

  // Load tasks from API once user is authenticated
  useEffect(() => {
    if (!currentUser) return;
    api('/api/tasks')
      .then(res => res.json())
      .then(data => {
        setCalendarSyncErrors(data.calendarSyncErrors);   // calendar-2
        if (Array.isArray(data.tasks)) { setTasks(data.tasks); setTasksLoadFailed(false); setTasksLoaded(true); }
        else setTasksLoadFailed(true);
      })
      .catch(err => {
        console.error('Failed to load tasks:', err);
        // "nothing here" and "we could not load this" are different states: with
        // this false, an empty task list told her "No tasks due today — you're all
        // clear!" after a failed load (Carla, 2026-10-03).
        setTasksLoadFailed(true);
      });
  }, [currentUser?.id]);

  // Cross-device sync — re-fetch tasks every 30 s when authenticated and tab is visible
  useEffect(() => {
    if (!currentUser) return;
    let intervalId = null;

    const poll = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await api('/api/tasks');
        if (!res.ok) return;
        const data = await res.json();
        setCalendarSyncErrors(data.calendarSyncErrors);   // calendar-2: outside the unchanged-list check below
        if (!Array.isArray(data.tasks)) return;
        const fresh = chatTaskActions.withoutPending(data.tasks);   // decision 75: see above
        const incoming = fresh.map(t => `${t.id}:${t.updatedAt || ''}`).sort().join('|');
        setTasks(prev => {
          const current = prev.map(t => `${t.id}:${t.updatedAt || ''}`).sort().join('|');
          return current === incoming ? prev : fresh;
        });
      } catch (_) {}
    };

    const startPolling = () => { intervalId = setInterval(poll, 30000); };
    const stopPolling = () => { clearInterval(intervalId); intervalId = null; };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') { poll(); startPolling(); }
      else { stopPolling(); }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (document.visibilityState === 'visible') startPolling();

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser?.id]);

  // Priority auto-escalation — runs whenever tasks change, fires one API write per task.
  // Guard: task.priority already "High" (persisted) OR escalatingRef (in-flight) → skip.
  // Logging and API writes live here, NOT in the render-path escalatePriorityIfDueSoon.
  useEffect(() => {
    if (!currentUser || !tasks.length) return;
    const now = new Date();
    tasks.forEach(task => {
      if (task.completed || task.priority === 'High') return;
      if (!task.dueDate && !task.date) return;
      if (escalatingRef.current.has(task.id)) return;

      const diff = (new Date(task.dueDate || task.date) - now) / (1000 * 60 * 60 * 24);
      if (diff >= 0) return; // visual-only: only flag already-overdue tasks

      console.log(`🚨 Auto-escalating task "${task.title}" to High priority (overdue)`);
      escalatingRef.current.add(task.id);
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, priority: 'High' } : t));
    });
  }, [tasks, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // bfcache restore — when the browser restores a page from the back-forward
  // cache, pageshow fires with event.persisted = true. The access token module
  // variable survives in memory but may have expired while frozen. Proactively
  // refresh so the next API call doesn't fail with a 401.
  useEffect(() => {
    const handlePageShow = (e) => {
      if (!e.persisted) return;
      refreshAccessToken(); // fire-and-forget; aidey:auth:expired and network-error paths handle outcomes
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Route incoming notifications by priority tier
  useEffect(() => {
    if (!notifications || !currentUser) return;

    const undelivered = notifications.filter(n => !deliveredNotifIdsRef.current.has(n.id));
    if (!undelivered.length) return;

    if (!notifInitRef.current) {
      // First load — initialise the seen set without triggering any delivery
      notifInitRef.current = true;
      undelivered.forEach(n => deliveredNotifIdsRef.current.add(n.id));
      return;
    }

    undelivered.forEach(n => deliveredNotifIdsRef.current.add(n.id));

    // RE-TEST ITEM 24: this router used to DISMISS every high/urgent notice the
    // moment it spoke it, which is why her booking notice was never in the bell.
    // routeNotifications decides what may be cleared, and urgent/high never are.
    const { urgent, dismissIds } = routeNotifications(undelivered);

    // Low priority only — folded into the next briefing.
    if (dismissIds.length) dismissNotifications(dismissIds);

    // Normal priority — left in place; NotificationFeed reads them from context.

    // High/urgent — open Aidey and deliver conversationally. NOT cleared:
    // Carla's rule is that only her own action clears one.
    if (urgent.length) {
      const lines = urgent.map(n => `**${n.title}**: ${n.message}`).join('\n\n');
      const msg = urgent.length === 1
        ? `${urgent[0].title}, ${urgent[0].message}`
        : `I have ${urgent.length} important updates:\n\n${lines}`;
      const notifId = `notif-${Date.now()}`;
      // Decision 40: on a phone this is a toast; its "Open" is the tap that opens the chat.
      deliverUnasked({
        phone: isPhoneViewport(),
        toastMessage: urgent.length === 1 ? `${urgent[0].title}: ${urgent[0].message}` : `You have ${urgent.length} important updates.`,
        open: () => {
          setNotificationProactive({ id: notifId, message: msg });
          setShowAideyChat(true);
          setIsChatMinimised(false);
        },
      });
    }
  }, [notifications, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load notes and note categories from API once user is authenticated
  useEffect(() => {
    if (!currentUser) return;
    api('/api/notes')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.notes)) setQuickNotes(data.notes);
      })
      .catch(err => console.error('Failed to load notes:', err));
    api('/api/note-categories')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.categories)) setNoteCategories(data.categories);
      })
      .catch(err => console.error('Failed to load note categories:', err));
  }, [currentUser?.id]);

  // Refresh notes when another component creates/modifies one
  useEffect(() => {
    const onNotesUpdated = () => {
      api('/api/notes')
        .then(res => res.json())
        .then(data => { if (Array.isArray(data.notes)) setQuickNotes(data.notes); })
        .catch(err => console.error('Failed to refresh notes:', err));
    };
    window.addEventListener("aidey:notesUpdated", onNotesUpdated);
    return () => window.removeEventListener("aidey:notesUpdated", onNotesUpdated);
  }, []);

  // Navigate to a specific settings section from anywhere in the app
  useEffect(() => {
    function handleGotoSettings(e) {
      setSettingsInitialSection(e.detail || null);
      setFilter('Settings');
    }
    window.addEventListener('aidey:goto-settings', handleGotoSettings);
    return () => window.removeEventListener('aidey:goto-settings', handleGotoSettings);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Item 8 (2026-10-12): a push that arrives with no page of its own (sent before every push had
  // a url) opens the Notifications page, never the app root and never the chat.
  const pushUrl = (url) => (!url || url === '/' ? '/notifications' : url);
  // A tapped push marks its notification read, as a tap in the bell does. Before sign-in it waits.
  const pendingPushRead = useRef(null);
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;
  const markPushRead = (nc) => {
    const id = nc && nc.notificationId;
    if (!id) return;
    if (currentUserRef.current) markNotificationAsRead(id);
    else pendingPushRead.current = id;
  };
  useEffect(() => {
    if (currentUser && pendingPushRead.current) { markNotificationAsRead(pendingPushRead.current); pendingPushRead.current = null; }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notification deep-link: handles two paths:
  //  1. SW posts NOTIFICATION_NAVIGATE when a push click lands on an already-open window.
  //  2. aidey:navigate is dispatched by CurrentUserContext.login() to consume a stored
  //     returnTo after the user completes login on a previously-intercepted deep-link.
  useEffect(() => {
    function applyNavigate(path) {
      try {
        const parsed = new URL(path, window.location.origin);
        const view = viewForPath(parsed.pathname);
        if (view === NOT_FOUND) return false;
        if (!currentUser) {
          // Logged out — park the destination (incl. ?entity=id) so login can pick it up.
          try { sessionStorage.setItem('aidey_return_to', parsed.pathname + parsed.search); } catch {}
          return true;
        }
        // A link to a section this workspace does not have (a Business page from a
        // Personal workspace, or the reverse) opens Notifications instead of a page
        // that is not there, and does not publish the item link.
        const target = notificationView(view, workspaceIsPersonalRef.current);
        if (target !== view) {
          setFilter(target);
          window.history.replaceState({}, '', VIEW_TO_PATH[target]);
          return true;
        }
        setFilter(view);
        // Block D20: publish the item deep-link (if any) so the section opens the entity.
        const ent = entityFromUrl(path);
        // Block D23 fix (a): PRESERVE the ?<entity>=<id> query in the URL (do not strip to
        // pathname) so a remount — e.g. the SW's post-deploy force-reload — can recover the deep
        // link from the URL. clearPendingDeepLink() strips it once the section opens the item.
        // Other params (section, nc) are intentionally not carried forward.
        const cleanUrl = ent ? `${parsed.pathname}?${ent.entity}=${encodeURIComponent(ent.id)}` : parsed.pathname;
        window.history.replaceState({}, '', cleanUrl);
        const section = parsed.searchParams.get('section');
        if (view === 'Settings' && section) setSettingsInitialSection(section);
        if (ent) setDeepLink(ent.entity, ent.id);
        return true;
      } catch { return false; }
    }

    // Carla's item 8 (2026-10-12): a tapped push opens its page (the server sends every push a
    // url: the item, else its section) and marks its notification read. It NEVER opens the Aidey
    // chat: the notifContext used to prime the chat when the url named no item.
    function onSwMessage(e) {
      if (e.data?.type === 'NOTIFICATION_NAVIGATE') {
        applyNavigate(pushUrl(e.data.url));
        markPushRead(e.data.notifContext);
      }
    }
    function onNavigate(e) { applyNavigate(e.detail); }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }
    window.addEventListener('aidey:navigate', onNavigate);
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      }
      window.removeEventListener('aidey:navigate', onNavigate);
    };
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle ?nc= query param set by SW when opening a fresh window with notifContext
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const nc = params.get('nc');
      // Block D23 fix (a): keep the ?<entity>= param in the URL (the section consumes+clears it
      // via clearPendingDeepLink so a remount can still recover it); drop only the ?nc= param.
      if (nc) {
        const u = new URL(window.location.href);
        u.searchParams.delete('nc');
        window.history.replaceState({}, '', u.pathname + (u.search || ''));
      }
      if (!nc) return;
      // Item 8: the page is the url's (seeded by deepLink.js and PATH_TO_VIEW); the context only
      // says which notification was tapped, so it is marked read. Never the chat.
      markPushRead(JSON.parse(decodeURIComponent(nc)));
    } catch {}
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Block D21: cold-start deep-link handoff. iOS PWA drops the SW's openWindow URL on a cold
  // tap, so the SW also stashed the destination in IndexedDB (see sw.js notificationclick).
  // Read it once on boot and drive the SAME navigate + notifContext path the ?nc= and
  // NOTIFICATION_NAVIGATE handlers use. Runs once (empty deps): applyNavigate handles a
  // not-yet-logged-in user by parking the destination in returnTo, so we don't need currentUser
  // here, and reading-and-deleting is one-shot — keying on currentUser could re-run and lose it.
  useEffect(() => {
    let cancelled = false;
    readPendingDeepLink().then((rec) => {
      if (cancelled || !rec || !rec.url) return;
      // Navigate the section into view + publish the item deep-link (applyNavigate → setDeepLink).
      // Item 8: the url is the destination (the item, else its section), never the chat.
      window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: pushUrl(rec.url) }));
      markPushRead(rec.notifContext);
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSettingsDropdownOpen &&
        !event.target.closest('.settings-dropdown-btn') &&
        !event.target.closest('.mobile-menu-account') &&
        !event.target.closest('.settings-dropdown-menu')) {
        setIsSettingsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSettingsDropdownOpen]);

  // fix(toast), 2026-10-07: App's toasts go through utils/toast.js, the one toast (its own state and
  // markup here rendered a second, differently styled toast).
  function showToast(msg) {
    const m = typeof msg === 'string' ? { type: 'info', message: msg } : (msg || {});
    showSharedToast(m.message, { type: m.type || 'info' });
  }

  // Add event listeners for email actions
  useEffect(() => {
    const handleCreateTaskFromEmail = async (event) => {
      const { task } = event.detail;
      const newTask = {
        title: task.title,
        details: task.description || '',
        ...(task.priority ? { priority: task.priority } : {}),   // none chosen: her usual one (lib/learntDefaults), else Medium
        completed: false,
        type: task.type || '',
        date: task.dueDate || null,
        startTime: null,
        deadlineDate: task.dueDate || null,
        deadlineTime: null,
        assignedTeam: [],
        meetingType: task.meetingType || null,
        location: '',
        emailId: task.emailId,
        emailSubject: task.emailSubject,
        emailFrom: task.emailFrom,
        createdAt: task.createdAt,
      };
      try {
        const res = await api('/api/tasks', { method: 'POST', body: JSON.stringify(newTask) });
        if (!res.ok) { console.error('Failed to persist email task:', res.status); return; }
        const data = await res.json();
        const savedTask = data.task || newTask;
        setTasks(prev => [...prev, savedTask]);
        window.dispatchEvent(new Event('aidey:tasksUpdated'));
        showToast(`✅ Task "${task.title}" created from email!`);
      } catch (err) {
        console.error('Failed to persist email task:', err);
      }
    };

    const handleCreateMeetingFromEmail = (event) => {
      const { meeting } = event.detail;

      // Pre-fill task form with meeting data
      setTitle(meeting.title);
      setDetails(meeting.description || '');
      setTaskType('meeting');
      setMeetingType('Google Meet');
      setShowModal(true);

      showToast(`📅 Meeting form pre-filled from email!`);
    };

    const handleSetReminderFromEmail = async (event) => {
      const { reminder } = event.detail;
      const reminderTask = {
        title: `⏰ ${reminder.title}`,
        details: `Reminder set from email`,
        priority: 'High',
        completed: false,
        type: 'reminder',
        date: reminder.reminderDate,
        deadlineDate: reminder.reminderDate,
        emailId: reminder.emailId,
        createdAt: reminder.createdAt,
      };
      try {
        const res = await api('/api/tasks', { method: 'POST', body: JSON.stringify(reminderTask) });
        if (!res.ok) { console.error('Failed to persist reminder task:', res.status); return; }
        const data = await res.json();
        const savedTask = data.task || reminderTask;
        setTasks(prev => [...prev, savedTask]);
        window.dispatchEvent(new Event('aidey:tasksUpdated'));
        showToast(`⏰ Reminder set for "${reminder.title}"!`);
      } catch (err) {
        console.error('Failed to persist reminder task:', err);
      }
    };

    // Add event listeners (email-derived actions)
    window.addEventListener('createTaskFromEmail', handleCreateTaskFromEmail);
    window.addEventListener('createMeetingFromEmail', handleCreateMeetingFromEmail);
    window.addEventListener('setReminderFromEmail', handleSetReminderFromEmail);

    // Cleanup
    return () => {
      window.removeEventListener('createTaskFromEmail', handleCreateTaskFromEmail);
      window.removeEventListener('createMeetingFromEmail', handleCreateMeetingFromEmail);
      window.removeEventListener('setReminderFromEmail', handleSetReminderFromEmail);
    };
  }, [tasks]);

  // Email inbox navigation from Aidey chat + unread count polling
  useEffect(() => {
    const openInbox = () => setFilter('EmailDashboard');
    window.addEventListener('aidey:openEmailInbox', openInbox);
    const updateCount = (e) => setEmailUnreadCount(e.detail.count);
    window.addEventListener('aidey:emailUnreadCount', updateCount);

    // Only poll when the user is confirmed logged in and a token exists
    let poll;
    if (currentUser) {
      const token = sessionStorage.getItem('aidey_at') || localStorage.getItem('aidey_at');
      if (token) {
        const fetchUnreadCount = async () => {
          try {
            const res = await api('/api/email/inbox/unread-count');
            if (res.ok) {
              const data = await res.json();
              if (data.count != null) setEmailUnreadCount(data.count);
            }
          } catch (e) {
            // Silently ignore — user may not have email connected
          }
        };
        fetchUnreadCount();
        poll = setInterval(fetchUnreadCount, 2 * 60 * 1000);
      }
    }

    return () => {
      window.removeEventListener('aidey:openEmailInbox', openInbox);
      window.removeEventListener('aidey:emailUnreadCount', updateCount);
      if (poll) clearInterval(poll);
    };
  }, [currentUser]); // Re-run when user logs in/out

  // Follow-up panel actions: open chat with prefilled message or open task-create modal
  useEffect(() => {
    const handleOpenChat = (e) => {
      const { message = '', notifContext: ctx = null } = e.detail || {};
      setPrefillChatInput(message);
      setNotifContext(ctx);
      // Decision 40: an intro that waited on a phone is shown now, when she opened the chat
      // herself (the Ask button: no message, no notification), and only now recorded as seen.
      const waitingIntro = !message && !ctx ? takeQueuedChatIntro() : null;
      if (waitingIntro) {
        setProactiveMessage({ id: `intro-${waitingIntro.tourId}-${Date.now()}`, kind: 'intro', body: waitingIntro.body });
        waitingIntro.record?.();
      }
      setPopupSessionId(newSessionId());
      setShowAideyChat(true);
      setIsChatMinimised(false);
      localStorage.removeItem('aidey-chat-minimised');
    };

    const handleCreateTask = (e) => {
      const { title: t = '', taskType: tt = '' } = e.detail || {};
      setEditingId(null);
      setTitle(t);
      setDetails('');
      setPriority('Medium');
      setStartDate(null);
      setStartTime(null);
      setDeadlineDate(null);
      setDeadlineTime(null);
      setTaskType(tt);
      setAssignedTeam([]);
      setReminderOffset(5);
      setCustomReminder('');
      setTaskProject(null);
      setShowModal(true);
    };

    // INTRO-2: a screen's first-use chat intro. Reuses the proactiveMessage bridge
    // with a kind:'intro' payload, so SuperAideyChat renders `body` as an assistant
    // message. The intro hook has already gated on the tour being terminal, so this
    // can never co-fire with a tour.
    const handleChatIntro = (e) => {
      const { tourId, body } = e.detail || {};
      if (!body) return;
      // Decision 40: never opens on a phone (useChatIntro does not send it there; this is the
      // second door). It waits for the next time she opens the chat herself.
      if (isPhoneViewport()) { queueChatIntro({ tourId, body }); return; }
      setProactiveMessage({ id: `intro-${tourId}-${Date.now()}`, kind: 'intro', body });
      setPopupSessionId(newSessionId());
      setShowAideyChat(true);
      setIsChatMinimised(false);
      localStorage.removeItem('aidey-chat-minimised');
    };

    window.addEventListener('aidey:open-chat', handleOpenChat);
    window.addEventListener('aidey:create-task', handleCreateTask);
    window.addEventListener('aidey:chat-intro', handleChatIntro);
    return () => {
      window.removeEventListener('aidey:open-chat', handleOpenChat);
      window.removeEventListener('aidey:create-task', handleCreateTask);
      window.removeEventListener('aidey:chat-intro', handleChatIntro);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to get user preferences from localStorage
  const getUserPreferences = () => {
    try {
      const storedProfile = localStorage.getItem("userProfile");
      if (storedProfile) {
        const profile = JSON.parse(storedProfile);
        return profile.preferences || {};
      }
    } catch (error) {
      console.error("Error reading user preferences:", error);
    }
    return {};
  };


  // Helper to create a new task object and add it to state
  const createAndAddTask = ({
    title,
    datetime,
    deadline,
    details = "",
    priority,   // none chosen: the server gives her usual one (Aidey Backend/lib/learntDefaults.js), else Medium
    taskType = "",
    reminderOffset = 5,
    completed = false,
    assignedTeam = [],
    busy = true,
    recurrence = null, // NEW: Handle recurring tasks
    meetingType = null, // NEW: Meeting type (Zoom, Google Meet, etc.)
    location = "", // NEW: Location field (URL or address)
    id = null, // NEW: Optional ID parameter
    projectId = null,  // STEP 1B: Add project support
    sprintId = null,   // STEP 3A: Add sprint support
    storyPoints = 0,   // STEP 3A: Add story points for Agile
    tags = [],         // Add tags support
    timeTracked = [],  // STEP 3B: Add time tracking support
    isPersonal = false,
    householdMemberId = null,
  }) => {

    // Always store as local ISO string (no Z)
    const toLocalISOString = (date) => {
      if (!date) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return (
        date.getFullYear() +
        "-" +
        pad(date.getMonth() + 1) +
        "-" +
        pad(date.getDate()) +
        "T" +
        pad(date.getHours()) +
        ":" +
        pad(date.getMinutes()) +
        ":" +
        pad(date.getSeconds())
      );
    };

    // ✅ ADD USER ASSOCIATION
    const newTask = {
      id: id || Date.now(), // Use provided ID or generate new one
      title,
      datetime: typeof datetime === "string" ? datetime : toLocalISOString(datetime),
      deadline: typeof deadline === "string" ? deadline : toLocalISOString(deadline),
      date: typeof datetime === "string"
        ? datetime.slice(0, 10)
        : toLocalDateStr(datetime),
      time: typeof datetime === "string"
        ? datetime.slice(11, 16)
        : datetime.toTimeString().slice(0, 5),
      details,
      priority,
      taskType,
      reminderOffset,
      completed,
      assignedTeam,
      householdMemberId,
      busy,
      recurrence, // Store recurrence information
      meetingType, // Store meeting type
      location, // Store location (URL or address)
      // ✅ ADD USER ASSOCIATION
      userId: currentUser?.id,
      createdBy: currentUser?.id,
      // ✅ ADD PROJECT SUPPORT (STEP 1B)
      projectId,
      sprintId,
      storyPoints,
      tags,
      timeTracked,
      isPersonal,
      dueDate: deadline // Alias for priority escalation function
    };

    // 🐛 DEBUG: Log the new task creation
    console.log("🎯 Creating new task:", {
      taskId: newTask.id,
      title: newTask.title,
      date: newTask.date,
      time: newTask.time,
      location: newTask.location,
      meetingType: newTask.meetingType,
      priority: newTask.priority,
      userId: newTask.userId,
      createdBy: newTask.createdBy,
      currentUser: currentUser?.id
    });


    const savePromise = api('/api/tasks', {
      method: 'POST',
      body: JSON.stringify(newTask),
    }).then(async (res) => {
      if (!res.ok) {
        console.error('Failed to persist task to API:', res.status);
        return null;
      }
      const data = await res.json();
      const savedTask = data.task || newTask;
      setTasks((prev) => [...prev, savedTask]);
      window.dispatchEvent(new Event('aidey:tasksUpdated'));
      return savedTask;
    }).catch(err => {
      // UI-5: this was console-only, so a task that never saved looked saved. api() throws on
      // every non-ok answer, so the server's own message is shown here, for every caller.
      console.error('Failed to persist task to API:', err);
      showErrorToast(messageFromError(err, `Could not save "${newTask.title || 'that task'}". Please try again.`));
      return null;
    });

    return savePromise;
  };

  // STEP 5: Auto-Escalate Priority When Near Deadline
  // Visual-only: shows High in the UI optimistically while the useEffect persists it.
  // No log here — logging fires once in the effect when the API write is committed.
  const escalatePriorityIfDueSoon = (task) => {
    if (!task.dueDate && !task.date) return task;
    const diff = (new Date(task.dueDate || task.date) - new Date()) / (1000 * 60 * 60 * 24);
    if (diff <= 2 && task.priority !== 'High') return { ...task, priority: 'High' };
    return task;
  };

  // Apply escalation to all tasks
  const applyPriorityEscalation = (taskList) => {
    return taskList.map(escalatePriorityIfDueSoon);
  };

  // STEP 4: Helper functions to merge project tasks with main app tasks
  const getAllTasksIncludingProject = () => {
    // Get project tasks and convert them to main app task format
    const projectTasks = projectManager.getAllTasks().map(projectTask => ({
      id: `project-${projectTask.id}`,
      title: projectTask.title,
      datetime: projectTask.dueDate,
      deadline: projectTask.dueDate,
      date: projectTask.dueDate ? projectTask.dueDate.slice(0, 10) : null,
      time: projectTask.dueDate ? projectTask.dueDate.slice(11, 16) : "09:00",
      details: projectTask.description || "",
      priority: projectTask.priority,
      taskType: "project-task",
      reminderOffset: 5,
      completed: projectTask.status === "done",
      assignedTeam: projectTask.assignedTo || [],
      busy: true,
      recurrence: null,
      meetingType: null,
      location: "",
      userId: currentUser?.id,
      createdBy: projectTask.createdBy,
      projectId: projectTask.projectId,
      sprintId: projectTask.sprintId,
      storyPoints: projectTask.storyPoints || 0,
      tags: projectTask.tags || [],
      timeTracked: projectTask.timeTracked || [],
      dueDate: projectTask.dueDate,
      // Add project info for display
      projectName: projectManager.getProject(projectTask.projectId)?.name,
      projectColor: projectManager.getProject(projectTask.projectId)?.color
    }));

    // Merge with regular tasks and apply priority escalation
    return applyPriorityEscalation([...tasks, ...projectTasks]);
  };

  // Get tasks for a specific user (including project tasks they're assigned to)
  const getUserTasks = (userId = null) => {
    const targetUserId = userId || currentUser?.id;
    if (!targetUserId) return [];

    return getAllTasksIncludingProject().filter(task => {
      // Include if user created it or is assigned to it
      return task.userId === targetUserId ||
        task.createdBy === targetUserId ||
        (task.assignedTeam && task.assignedTeam.includes(targetUserId));
    });
  };

  // Enhanced tasksForDate that includes project tasks
  const getTasksForDate = (date) => {
    const allTasks = getAllTasksIncludingProject();
    const dateString = toLocalDateStr(date);

    return allTasks.filter(task => {
      if (!task.date) return false;
      return task.date === dateString;
    });
  };

  // STEP 3B: Time tracking helpers
  const logTimeToTask = (taskId, userId, minutes, note = "") => {
    // Check if it's a project task
    if (taskId.toString().startsWith('project-')) {
      const projectTaskId = parseInt(taskId.replace('project-', ''));
      projectManager.logTime(projectTaskId, userId, minutes, note);
    } else {
      // Handle regular app tasks
      setTasks(prevTasks =>
        prevTasks.map(task => {
          if (task.id === taskId) {
            const timeEntry = {
              userId,
              minutes,
              date: new Date().toISOString().slice(0, 10),
              note,
              timestamp: Date.now()
            };
            return {
              ...task,
              timeTracked: [...(task.timeTracked || []), timeEntry]
            };
          }
          return task;
        })
      );
    }
    console.log(`⏱️ Logged ${minutes} minutes to task ${taskId} by user ${userId}`);
  };

  const getTodayTasks = () => {
    const todayISOString = toLocalDateStr(today);

    const todayTasks = tasks
      .filter(
        (task) => {
          if (task.completed) {
            return false;
          }

          // Check if task date matches today using consistent ISO format
          if (task.date === todayISOString) {
            return true;
          }

          // Fallback: Try multiple date formats for compatibility
          let taskDate;
          if (task.datetime) {
            taskDate = new Date(task.datetime);
          } else if (task.date && task.time) {
            taskDate = new Date(`${task.date}T${task.time}`);
          } else if (task.date) {
            taskDate = new Date(task.date);
          } else {
            return false;
          }

          // Check if the date is valid and matches today
          const isValid = taskDate instanceof Date && !isNaN(taskDate);
          const matchesToday = isValid && toLocalDateStr(taskDate) === todayISOString;

          return matchesToday;
        }
      )
      .sort((a, b) => {
        // Strict time-ascending — priority is a badge only, not a sort key.
        const getTaskDate = (task) => {
          if (task.datetime) return new Date(task.datetime);
          if (task.date && task.time) return new Date(`${task.date}T${task.time}`);
          if (task.date) return new Date(task.date);
          return new Date();
        };

        return getTaskDate(a) - getTaskDate(b);
      });

    // STEP 5: Apply priority escalation to today's tasks
    return applyPriorityEscalation(todayTasks);
  };
  // === AideyChat handlers ===
  const handleAideyAddTask = (json) => {
    if (!json.date || !json.title) {
      alert("Missing date or title in add_task JSON!");
      return;
    }
    const timeString = json.time || "09:00";
    let datetimeStr = `${json.date}T${timeString}`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(datetimeStr)) {
      datetimeStr += ":00";
    }
    // Do NOT add Z here!
    let datetime = new Date(datetimeStr);

    let deadline;
    if (json.deadline) {
      let deadlineStr = json.deadline;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(deadlineStr)) {
        deadlineStr += ":00";
      }
      // Do NOT add Z here!
      deadline = new Date(deadlineStr);
    } else {
      deadline = new Date(datetime.getTime() + 60 * 60 * 1000);
    }

    if (isNaN(datetime) || isNaN(deadline)) {
      showErrorToast("Invalid date/time format from Aidey. Got: " + datetimeStr + " / " + (json.deadline || ""));
      return;
    }

    createAndAddTask({
      title: json.title,
      datetime,
      deadline,
      details: json.details || "",
      priority: json.priority || undefined,
      taskType: json.taskType || "",
      reminderOffset: json.reminderOffset || 5,
      completed: false,
      assignedTeam: json.assignedTeam || [],
      // ✅ ADD MISSING PROPERTIES
      meetingType: json.meetingType || "",
      location: json.location || "",
      busy: json.busy !== false,
      recurrence: json.recurrence || null,
      // ✅ STEP 4: ADD PROJECT SUPPORT FROM AIDEY CHAT
      projectId: json.projectId || null,
      sprintId: json.sprintId || null,
      storyPoints: json.storyPoints || 0,
      tags: json.tags || []
    });
  };

  const handleAideyEditTask = (editObj, onNoMatch) => {
    let targetTask = null;
    const newTasks = tasks.map(task => {
      const taskDate = task.date || (task.datetime && toLocalDateStr(new Date(task.datetime)));
      const taskTime = task.time || (task.datetime && new Date(task.datetime).toTimeString().slice(0, 5));
      if (
        task.title === editObj.title &&
        taskDate === editObj.date &&
        taskTime === editObj.time
      ) {
        const merged = { ...task, ...editObj };
        if (editObj.datetime) {
          const dt = new Date(editObj.datetime);
          merged.date = toLocalDateStr(dt);
          merged.time = dt.toTimeString().slice(0, 5);
        }
        targetTask = merged;
        return merged;
      }
      return task;
    });

    if (targetTask) {
      setTasks(newTasks);
      api(`/api/tasks/${targetTask.id}`, {
        method: 'PUT',
        body: JSON.stringify(targetTask),
      }).catch(err => console.error('Failed to update task in API:', err));
    }

    setTimeout(() => {
      if (!targetTask && typeof onNoMatch === "function") onNoMatch();
    }, 0);
  };

  const handleAideyDeleteTask = (json) => {
    if (json.id) {
      setTasks(prev => prev.filter(task => task.id !== json.id));
      api(`/api/tasks/${json.id}`, { method: 'DELETE' })
        .catch(err => console.error('Failed to delete task from API:', err));
      return;
    }
    if (json.title && json.date) {
      const toDelete = tasks.find(task =>
        task.title === json.title &&
        toLocalDateStr(new Date(task.datetime)) === json.date
      );
      if (toDelete) {
        setTasks(prev => prev.filter(task => task.id !== toDelete.id));
        api(`/api/tasks/${toDelete.id}`, { method: 'DELETE' })
          .catch(err => console.error('Failed to delete task from API:', err));
        return;
      }
    }
    showErrorToast("Couldn't find a task to delete. Missing id or title/date.");
  };
  const resetAndCloseTaskModal = () => {
    setTaskFormClash(null);
    setShowModal(false);
    setEditingId(null);
    setTitle("");
    setDetails("");
    setPriority("Medium");
    setStartDate(null);
    setStartTime(null);
    setDeadlineDate(null);
    setDeadlineTime(null);
    setTaskType("");
    setAssignedTeam([]);
    setHouseholdMemberId(null);
    setReminderOffset(5);
    setBusy(true);
    setIsPersonal(false);
    setRecurrence(null);
    setTaskProject(null);
    const userPrefs = getUserPreferences();
    setMeetingType(userPrefs.defaultMeetingType === "" ? null : userPrefs.defaultMeetingType);
    setLocation("");
  };

  const addNote = (categoryId) => {
    if (newNote.trim() === "") return;
    const note = {
      id: String(Date.now()),
      title: "",
      text: newNote.trim(),
      blocks: [],
      created: new Date().toISOString(),
      categoryId: categoryId || null,
    };
    setQuickNotes([note, ...quickNotes]);
    setNewNote("");
    api('/api/notes', {
      method: 'POST',
      body: JSON.stringify(note),
    }).catch(err => console.error('Failed to save note to API:', err));
  };

  const deleteNote = (id) => {
    setQuickNotes(quickNotes.filter((note) => note.id !== id));
    api(`/api/notes/${id}`, { method: 'DELETE' })
      .catch(err => console.error('Failed to delete note from API:', err));
  };


  // Load attachments when the view modal opens on a task
  useEffect(() => {
    if (!viewTask?.id) { setViewTaskAttachments([]); return; }
    api(`/api/tasks/${viewTask.id}/attachments`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.attachments)) setViewTaskAttachments(d.attachments); })
      .catch(() => {});
  }, [viewTask?.id]);

  const handleDeleteViewTaskAttachment = async (attachId) => {
    if (!viewTask?.id) return;
    await api(`/api/tasks/${viewTask.id}/attachments/${attachId}`, { method: 'DELETE' });
    setViewTaskAttachments(prev => prev.filter(a => a.id !== attachId));
  };

  // For chatbot: receives a plain object, NOT an event
  const handleAddTaskFromChat = async (taskObj, addChatReply = () => { }) => {

    // 🐛 DEBUG: Log what we received from chat
    console.log("💬 handleAddTaskFromChat received:", {
      taskObj,
      hasTitle: !!taskObj.title,
      hasDate: !!taskObj.date,
      hasTime: !!taskObj.time,
      location: taskObj.location,
      meetingType: taskObj.meetingType,
      meetingMode: taskObj.meetingMode,
      priority: taskObj.priority
    });

    // 1️⃣ Check for required fields (title, date, time)
    if (!taskObj.title || !taskObj.date || !taskObj.time) {
      addChatReply("❗ Please provide title, date, and time first.");
      return { ok: false };
    }

    // 2️⃣ Apply defaults for optional fields — never block task creation over these
    // No priority default here: a task with none takes her usual one on the server (lib/learntDefaults).
    if (taskObj.reminderOffset === undefined) taskObj.reminderOffset = 15;
    if (taskObj.details === undefined) taskObj.details = "";
    if (taskObj.location === undefined) taskObj.location = "";

    const newStart = new Date(`${taskObj.date}T${taskObj.time}`);
    const durationMs = (taskObj.durationMinutes || 60) * 60 * 1000;
    const newEnd = taskObj.deadline
      ? new Date(taskObj.deadline)
      : new Date(newStart.getTime() + durationMs);

    // Conflict check already handled by SuperAideyChat — skip here to respect override/swap decisions

    // 3️⃣ Persist task to DB — only confirm success after API response
    const newTaskId = Date.now();
    const savedTask = await createAndAddTask({
      title: taskObj.title,
      datetime: newStart,
      deadline: newEnd,
      details: taskObj.details || "",
      priority: taskObj.priority || undefined,
      taskType: taskObj.taskType || "",
      reminderOffset: taskObj.reminderOffset != null ? taskObj.reminderOffset : 5,
      completed: false,
      assignedTeam: taskObj.assignedTeam || [],
      busy: taskObj.busy !== false,
      recurrence: taskObj.recurrence || null,
      meetingType: taskObj.meetingType || "",
      location: taskObj.location || "",
      id: newTaskId,
    });

    if (!savedTask) {
      addChatReply(`⚠️ Failed to save the task, please check your connection and try again.`);
      return { ok: false };
    }

    addChatReply(`✅ Task "${taskObj.title}" scheduled for ${formatTaskClock(newStart)}.`);

    // 3.5️⃣ Handle Zoom meeting creation if needed
    try {
      if (taskObj.meetingType === "Zoom") {
        const { createZoomMeeting, isZoomConnected } = await import('./utils/zoomUtils');

        if (isZoomConnected()) {
          const zoomMeeting = await createZoomMeeting({
            topic: taskObj.title,
            start_time: `${taskObj.date}T${taskObj.time}:00`,
            duration: 60, // Default 1 hour
            agenda: taskObj.details || '',
          });

          if (zoomMeeting && zoomMeeting.join_url) {
            addChatReply(`🎥 Zoom meeting created: ${zoomMeeting.join_url}`);
            const updatedLocation = (taskObj.location || '').trim()
              ? `${taskObj.location} - Zoom: ${zoomMeeting.join_url}`
              : `Zoom: ${zoomMeeting.join_url}`;
            setTasks(prev => prev.map(t =>
              t.id === newTaskId ? { ...t, location: updatedLocation } : t
            ));
            api(`/api/tasks/${newTaskId}`, { method: 'PUT', body: JSON.stringify({ location: updatedLocation }) })
              .catch(err => console.error('Failed to persist Zoom link:', err));
          }
        } else {
          addChatReply(`⚠️ Zoom not connected. Please connect Zoom in Profile settings to create meeting links.`);
        }
      }
    } catch (error) {
      console.error('Zoom meeting creation failed:', error);
      addChatReply(`⚠️ Failed to create Zoom meeting. Please check your Zoom connection.`);
    }

    // 4️⃣ Now, and ONLY now, add to Google Calendar
    try {
      if (window.gapi && window.gapi.client && window.gapi.client.calendar) {
        const { createGoogleEventFromTask } = await import('./utils/calendarUtils');

        // Check if this should include Google Meet
        const includeGoogleMeet = taskObj.meetingType === "Google Meet";

        // Only send finalized details!
        const result = await createGoogleEventFromTask({
          ...taskObj,
          date: taskObj.date,
          time: taskObj.time,
          location: taskObj.location,
          reminderOffset: taskObj.reminderOffset || 5
        }, window.gapi, includeGoogleMeet);

        if (result && result.success) {
          addChatReply(`📅 ...and added to your Google Calendar!`);

          // If Google Meet link was created, add it to the task location
          if (result.googleMeetLink) {
            addChatReply(`📹 Google Meet link: ${result.googleMeetLink}`);
            const updatedLocation = (taskObj.location || '').trim()
              ? `${taskObj.location} - Google Meet: ${result.googleMeetLink}`
              : `Google Meet: ${result.googleMeetLink}`;
            setTasks(prev => prev.map(t =>
              t.id === newTaskId ? { ...t, location: updatedLocation } : t
            ));
            api(`/api/tasks/${newTaskId}`, { method: 'PUT', body: JSON.stringify({ location: updatedLocation }) })
              .catch(err => console.error('Failed to persist Google Meet link:', err));
          }
        }
      }
    } catch (error) {
      console.error('Google Calendar sync failed:', error);
      // Optional: Add a friendly message if sync fails
      // addChatReply("Could not sync with Google Calendar.");
    }
    // Release 7: the chat says "Scheduled" only on this (the task is saved; links are extras).
    return { ok: true, task: savedTask };
  };

  const handleMeetingRecorderCreateTask = (taskObj) => {
    const today = new Date().toISOString().slice(0, 10);
    const datetime = new Date(`${taskObj.date || today}T09:00:00`);
    const deadline = new Date(datetime.getTime() + 60 * 60 * 1000);
    createAndAddTask({
      title: taskObj.title,
      datetime,
      deadline,
      details: taskObj.details || "",
      priority: taskObj.priority || undefined,
      taskType: "Meeting",
      reminderOffset: 5,
      completed: false,
      assignedTeam: taskObj.assignedTeam || [],
      userId: taskObj.userId || null,
      busy: false,
    });
    window.dispatchEvent(new Event("aidey:tasksUpdated"));
  };

  // For the form: receives an event
  const handleAddTaskForm = (e) => {
    e.preventDefault();

    const start = combineDateTime(startDate, startTime); // returns a Date
    const end = combineDateTime(deadlineDate, deadlineTime); // returns a Date

    // fix(task-dates): an edit may leave the deadline empty, and saving keeps it empty.
    const formError = taskFormError({ title, start, end, editing: !!editingId });
    if (formError) {
      alert(formError);
      return;
    }
    // Normalize external calendar events to {datetime, deadline, title} for clash checking
    const normalizeExternal = (ev) => {
      // Google Calendar raw API event
      if (ev.start?.dateTime) {
        return { datetime: ev.start.dateTime, deadline: ev.end?.dateTime || ev.start.dateTime, title: ev.summary || "Calendar event", external: true };
      }
      // Outlook / iCal already in task shape (date + time → datetime)
      if (ev.date && (ev.time || ev.deadline)) {
        const dt = ev.datetime || `${ev.date}T${ev.time || '00:00'}:00`;
        return { datetime: dt, deadline: ev.deadline || dt, title: ev.title || ev.summary || "Calendar event", external: true };
      }
      if (ev.datetime && ev.deadline) {
        return { datetime: ev.datetime, deadline: ev.deadline, title: ev.title || "Calendar event", external: true };
      }
      return null;
    };

    const externalEvents = [
      ...(googleEvents || []),
      ...(icalEvents || []),
      ...(outlookEvents || []),
    ].map(normalizeExternal).filter(Boolean);

    const overlaps = (evStart, evEnd) => {
      try {
        const s = evStart instanceof Date ? evStart : new Date(evStart);
        const e = evEnd instanceof Date ? evEnd : new Date(evEnd);
        if (isNaN(s) || isNaN(e)) return false;
        return start < e && end > s;
      } catch { return false; }
    };

    // Check Aidey tasks first (they can be rescheduled)
    const conflictingAideyTask = tasks.find((task) => {
      if (editingId && task.id === editingId) return false;
      if (!task.datetime || !task.deadline) return false;
      return overlaps(task.datetime, task.deadline);
    });

    // Then check external calendar events
    const conflictingExternal = !conflictingAideyTask
      ? externalEvents.find((ev) => overlaps(ev.datetime, ev.deadline))
      : null;

    const hasClash = !!(conflictingAideyTask || conflictingExternal);
    const conflictingEvent = conflictingAideyTask || conflictingExternal;

    if (hasClash) {
      const conflictTitle = conflictingEvent.title || conflictingEvent.summary || 'another event';
      const conflictTime = conflictingEvent.datetime
        ? formatTaskClock(conflictingEvent.datetime)
        : (formatTaskClock(conflictingEvent.time) || null);
      setTaskFormClash({
        conflictTitle,
        conflictTime,
        another: Boolean(e._aideyAnother) && !editingId,   // UI-5: "Add another" survives Save anyway
        taskData: {
          title,
          datetime: start,
          deadline: end,
          details,
          priority,
          taskType,
          reminderOffset,
          completed: false,
          assignedTeam,
          householdMemberId,
          busy,
          recurrence,
          meetingType,
          location,
          isPersonal,
          projectId: taskProject,
        }
      });
      return;
    }

    // Decision 65: an occurrence of a repeating task first asks which occurrences change.
    if (editingId && editingOccurrence && !e._seriesScope) {
      setSeriesPrompt({ fromClash: false });
      return;
    }

    if (editingId) {
      // 🛠 EDIT EXISTING TASK
      const startISO = start instanceof Date ? start.toISOString() : start;
      const updatedFields = {
        title,
        datetime: start,
        deadline: end,
        date: startISO.slice(0, 10),
        time: start instanceof Date ? start.toTimeString().slice(0, 5) : startISO.slice(11, 16),
        details,
        priority,
        taskType,
        reminderOffset,
        meetingType,
        location,
        recurrence,
        isPersonal,
        householdMemberId,
        projectId: taskProject,
      };
      if (editingOccurrence) {
        // The server works out the rows (lib/taskSeries.js); the list reloads when it confirms.
        saveSeriesEdit({ seriesId: editingId, occurrence: editingOccurrence, scope: e._seriesScope, fields: updatedFields, repeatOffKeeps: repeatOffKeepsOccurrence(currentUser), source: 'task_edit' });
      } else {
      const prevTasks = tasks;
      setTasks(tasks.map((task) =>
        task.id === editingId ? { ...task, ...updatedFields } : task
      ));
      api(`/api/tasks/${editingId}`, {
        method: 'PUT',
        body: JSON.stringify({ ...updatedFields, dueChange: { source: 'task_edit' } }),   // the due-date history names the path
      }).then(res => {
        if (!res.ok) {
          setTasks(prevTasks);
          res.json().then(body => { if (body?.error) showErrorToast(body.error); }).catch(() => {});
        }
      }).catch(err => {
        console.error('Failed to update task in API:', err);
        setTasks(prevTasks);
        showErrorToast('Failed to save changes. Please try again.');
      });
      }
    } else {
      // ➕ ADD NEW TASK
      createAndAddTask({
        title,
        datetime: start,
        deadline: end,
        details,
        priority,
        taskType,
        reminderOffset,
        completed: false,
        assignedTeam,
        busy,
        recurrence,
        meetingType,
        location,
        isPersonal,
        householdMemberId,   // UI-5: the Personal "For" picker was never sent on create
        projectId: taskProject,
        id: e._aideyTaskId || undefined,
      });
    }

    // ✅ Reset form after both add or edit
    setTitle("");
    setDetails("");
    setPriority("Medium");
    setStartDate(null);
    setStartTime(null);
    setDeadlineDate(null);
    setDeadlineTime(null);
    setEditingId(null);
    // UI-5 "Add another after this": a new task saved, so the modal stays open, empty, for the next.
    const another = Boolean(e._aideyAnother) && !editingId;
    setShowModal(another);
    if (another) showSharedToast(`Added "${title}"`, { type: 'success' });
    setTaskType("");
    setAssignedTeam([]);
    setHouseholdMemberId(null);
    setReminderOffset(5);
    setIsPersonal(false);
    setTaskProject(null);
    // Reset to user's default meeting type preference
    const userPrefs = getUserPreferences();
    setMeetingType(userPrefs.defaultMeetingType === "" ? null : userPrefs.defaultMeetingType);
    setLocation("");
    setRecurrence(null);
  };

  const handleSaveAnyway = (seriesScope) => {
    if (!taskFormClash?.taskData) return;
    const { taskData } = taskFormClash;
    const scope = typeof seriesScope === 'string' ? seriesScope : null;
    // Decision 65: an occurrence of a repeating task first asks which occurrences change.
    if (editingId && editingOccurrence && !scope) {
      setSeriesPrompt({ fromClash: true });
      return;
    }

    if (editingId && editingOccurrence) {
      saveSeriesEdit({
        seriesId: editingId, occurrence: editingOccurrence, scope,
        fields: {
          title: taskData.title, datetime: taskData.datetime, deadline: taskData.deadline, details: taskData.details,
          priority: taskData.priority, taskType: taskData.taskType, reminderOffset: taskData.reminderOffset,
          meetingType: taskData.meetingType, location: taskData.location, recurrence: taskData.recurrence,
          householdMemberId: taskData.householdMemberId ?? null, projectId: taskData.projectId,
        },
        repeatOffKeeps: repeatOffKeepsOccurrence(currentUser),
      });
    } else if (editingId) {
      const start = taskData.datetime;
      const startISO = start instanceof Date ? start.toISOString() : String(start);
      const updatedFields = {
        title: taskData.title,
        datetime: start,
        deadline: taskData.deadline,
        date: startISO.slice(0, 10),
        time: start instanceof Date ? start.toTimeString().slice(0, 5) : startISO.slice(11, 16),
        details: taskData.details,
        priority: taskData.priority,
        taskType: taskData.taskType,
        reminderOffset: taskData.reminderOffset,
        meetingType: taskData.meetingType,
        location: taskData.location,
        recurrence: taskData.recurrence,
        isPersonal: taskData.isPersonal,
        householdMemberId: taskData.householdMemberId ?? null,   // UI-5: was dropped on Save anyway
        projectId: taskData.projectId,
      };
      const prevTasks = tasks;
      setTasks(tasks.map(t => t.id === editingId ? { ...t, ...updatedFields } : t));
      api(`/api/tasks/${editingId}`, {
        method: 'PUT',
        body: JSON.stringify({ ...updatedFields, dueChange: { source: 'task_edit' } }),   // the due-date history names the path
      }).then(res => {
        if (!res.ok) {
          setTasks(prevTasks);
          res.json().then(body => { if (body?.error) showErrorToast(body.error); }).catch(() => {});
        }
      }).catch(err => {
        console.error('Failed to update task:', err);
        setTasks(prevTasks);
      });
    } else {
      createAndAddTask(taskData);
    }

    setTaskFormClash(null);
    setTitle("");
    setDetails("");
    setPriority("Medium");
    setStartDate(null);
    setStartTime(null);
    setDeadlineDate(null);
    setDeadlineTime(null);
    setEditingId(null);
    // UI-5: the second way a new task saves; "Add another" keeps the modal open here too.
    const another = Boolean(taskFormClash?.another) && !editingId;
    setShowModal(another);
    if (another) showSharedToast(`Added "${taskData.title}"`, { type: 'success' });
    setTaskType("");
    setAssignedTeam([]);
    setHouseholdMemberId(null);
    setReminderOffset(5);
    setIsPersonal(false);
    setTaskProject(null);
    const userPrefs = getUserPreferences();
    setMeetingType(userPrefs.defaultMeetingType === "" ? null : userPrefs.defaultMeetingType);
    setLocation("");
    setRecurrence(null);
  };

  const handleChangeTime = () => setTaskFormClash(null);

  // Handle task editing (this will remain separate from adding)
  const handleEdit = (task) => {
    setViewTask(null); // Close the view modal
    const targetId = task.parentTaskId || task.id;

    // fix(task-dates): no deadline opens as no deadline (it opened as 1 January 1970).
    const { start, deadline } = taskFormDates(task);

    // Set all task fields for editing
    setTitle(task.title);
    setDetails(task.details || "");
    setPriority(task.priority || "Medium");
    setStartDate(start);
    setStartTime(start);
    setDeadlineDate(deadline);
    setDeadlineTime(deadline);
    setTaskType(task.taskType || ""); // ✅ ensures type is correct
    setAssignedTeam(task.assignedTeam || []); // ✅ ensures team selection is set
    setHouseholdMemberId(task.householdMemberId || null);
    setMeetingType(task.meetingType || null);
    setLocation(task.location || "");
    setIsPersonal(task.isPersonal || false);
    setRecurrence(task.recurrence || null);
    setTaskProject(task.projectId || null);
    setEditingId(targetId);
    setEditingOccurrence(occurrenceOf(task));   // decision 65: null unless it is one occurrence of a series
    setShowModal(true);
  };

  // Carla's decision 2 (2026-10-12): an occurrence of a repeating task is ticked, moved on the board
  // or deleted ON ITS OWN (utils/recurringEdit.js). These used to resolve the occurrence to its
  // series and PUT the series: ticking one completed every occurrence. null unless `id` is one.
  const occurrenceOnScreen = (id) => {
    const rendered = displayTasks.find((t) => String(t.id) === String(id));
    const occurrence = occurrenceOf(rendered);
    return occurrence ? { rendered, occurrence } : null;
  };
  // Shown at once; the server's row replaces it; a refusal (said by setOccurrenceState) puts it back.
  const changeOccurrence = async (occurrence, updates, send = setOccurrenceState) => {
    const before = tasks.find((t) => String(t.id) === String(occurrence.seriesId));
    if (!before) return false;
    setTasks((prev) => prev.map((t) => (t === before || String(t.id) === String(occurrence.seriesId) ? applyOccurrenceLocally(t, occurrence.start, updates) : t)));
    const row = await send({ seriesId: occurrence.seriesId, occurrence, updates });
    setTasks((prev) => prev.map((t) => {
      if (String(t.id) !== String(occurrence.seriesId)) return t;
      return row ? { ...t, recurrenceExceptions: row.recurrenceExceptions ?? t.recurrenceExceptions } : before;
    }));
    return !!row;
  };
  const deleteOccurrence = async (occurrence) => {
    setViewTask(null);
    // skipOccurrence says the outcome itself: the success, or a calendar that did not take it (M2).
    await changeOccurrence(occurrence, { skip: true }, skipOccurrence);
  };

  // Handle task deletion
  const handleDelete = (id) => {
    const targetId = resolveParentTaskId(id);
    setTasks(tasks.filter((task) => task.id !== targetId));
    setViewTask(null);
    api(`/api/tasks/${targetId}`, { method: 'DELETE' })
      .catch(err => console.error('Failed to delete task from API:', err));
  };

  // Mark task as complete
  const markComplete = (id) => {
    const occ = occurrenceOnScreen(id);
    const targetId = resolveParentTaskId(id);
    const task = occ ? occ.rendered : tasks.find(t => t.id === targetId);
    setViewTask(null);
    if (occ) {
      changeOccurrence(occ.occurrence, { completed: true });
    } else {
    setTasks(
      tasks.map((task) =>
        task.id === targetId ? { ...task, completed: true } : task,
      ),
    );
    api(`/api/tasks/${targetId}`, {
      method: 'PUT',
      body: JSON.stringify({ completed: true }),
    }).catch(err => console.error('Failed to mark task complete in API:', err));
    }

    // Post-meeting processing: open Aidey Chat with notes summary prompt. Decision 76: on a phone
    // it does not open by itself; a toast offers "Summarise", which opens it with the same prompt.
    if (task && task.taskType === 'meeting' && task.details && task.details.trim()) {
      setTimeout(() => {
        const prompt = `I just completed a meeting: ${task.title}. Meeting notes: ${task.details.trim()}. Please summarise the key points, identify any action items or follow-up tasks, and ask me if I'd like to send the summary to the meeting attendees.`;
        deliverUnasked({
          phone: isPhoneViewport(),
          open: () => { window.dispatchEvent(new CustomEvent('aidey:open-chat', { detail: { message: prompt } })); },
          toastMessage: `Meeting done: ${task.title}. Want a summary?`,
          actionLabel: 'Summarise',
        });
      }, 2000);
    }
  };

  // Undo completing a task
  const undoComplete = (id) => {
    const occ = occurrenceOnScreen(id);
    if (occ) { changeOccurrence(occ.occurrence, { completed: false }); return; }
    const targetId = resolveParentTaskId(id);
    setTasks(
      tasks.map((task) =>
        task.id === targetId ? { ...task, completed: false } : task,
      ),
    );
    api(`/api/tasks/${targetId}`, {
      method: 'PUT',
      body: JSON.stringify({ completed: false }),
    }).catch(err => console.error('Failed to undo task completion in API:', err));
  };

  // Move task between Kanban columns (updates completed + inProgress)
  // Block J: a move the server refuses is put back and said (it stayed moved on screen, with only a
  // console line, so the board showed a change that had not been saved).
  const moveTask = (id, updates) => {
    const occ = occurrenceOnScreen(id);
    if (occ) { changeOccurrence(occ.occurrence, updates); return; }
    const targetId = resolveParentTaskId(id);
    let before = null;
    setTasks(prev => prev.map(t => {
      if (t.id !== targetId) return t;
      before = Object.fromEntries(Object.keys(updates).map(k => [k, t[k]]));
      return { ...t, ...updates };
    }));
    api(`/api/tasks/${targetId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }).catch(err => {
      if (before) setTasks(prev => prev.map(t => (t.id === targetId ? { ...t, ...before } : t)));
      showErrorToast(messageFromError(err, 'That change was not saved.'));
    });
  };

  // Open the add-task modal with a blank form
  const openAddTaskModal = () => {
    setEditingId(null);
    setEditingOccurrence(null);
    setTitle("");
    setDetails("");
    setPriority("Medium");
    setStartDate(null);
    setStartTime(null);
    setDeadlineDate(null);
    setDeadlineTime(null);
    setTaskType("");
    setAssignedTeam([]);
    setReminderOffset(5);
    setCustomReminder("");
    setTaskProject(null);
    setShowModal(true);
  };

  // Tile content for the calendar view (month view)
  const tileContent = ({ date, view }, mergedDayTasks) => {
    if (view !== "month") return null;

    // mergedDayTasks comes from CalendarView (includes Google/iCal/Outlook events)
    // Fall back to local displayTasks only when called without the merged list
    const dayTasks = mergedDayTasks ?? tasksForDate(date, displayTasks);
    if (dayTasks.length === 0) return null;

    // Scheduler bookings (source field set by the backend)
    const bookingCount = dayTasks.filter(
      (task) => task.source === "scheduler_booking",
    ).length;

    // Priority counts exclude booking tasks and completed tasks
    const nonBookingTasks = dayTasks.filter(
      (task) => task.source !== "scheduler_booking" && !task.completed,
    );
    const highCount = nonBookingTasks.filter(
      (task) => task.priority === "High",
    ).length;
    const mediumCount = nonBookingTasks.filter(
      (task) => task.priority === "Medium",
    ).length;
    const lowCount = nonBookingTasks.filter((task) => task.priority === "Low").length;

    return (
      <div
        style={{
          display: "flex",
          gap: "4px",
          flexWrap: "wrap",
          marginTop: "4px",
        }}
      >
        {highCount > 0 && (
          <span className="priority-count high">{highCount}</span>
        )}
        {mediumCount > 0 && (
          <span className="priority-count medium">{mediumCount}</span>
        )}
        {lowCount > 0 && <span className="priority-count low">{lowCount}</span>}
        {bookingCount > 0 && (
          <span className="priority-count booking" title="Scheduler bookings">
            📅{bookingCount}
          </span>
        )}
      </div>
    );
  };

  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const recurrenceWindowStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 60);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [today]);

  const recurrenceWindowEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 365);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [today]);

  const displayTasks = useMemo(() => {
    return getRecurringTasksForDateRange(tasks, recurrenceWindowStart, recurrenceWindowEnd);
  }, [tasks, recurrenceWindowStart, recurrenceWindowEnd]);

  const resolveParentTaskId = (id) => {
    const rendered = displayTasks.find((t) => String(t.id) === String(id));
    return rendered?.parentTaskId || id;
  };

  const getTopFiveTasks = () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    // ✅ Filter for all uncompleted tasks due today — check both datetime and date fields
    const relevantTasks = tasks.filter((task) => {
      if (task.completed) return false;
      if (task.datetime) return new Date(task.datetime).toDateString() === new Date().toDateString();
      if (task.date) return task.date.slice(0, 10) === todayStr;
      return false;
    });

    // ✅ Sort by time and get top 5
    const sorted = relevantTasks.sort((a, b) =>
      (a.time || "").localeCompare(b.time || "")
    );

    return sorted.slice(0, 5);
  };
  const getTeamStatsForToday = () => {
    const stats = {}; // We'll store counts here
    const todayStr = today.toDateString(); // We use string format to compare dates

    tasks.forEach((task) => {
      if (!task.assignedTeam || !Array.isArray(task.assignedTeam)) return;

      const taskDateStr = new Date(task.datetime).toDateString();
      if (taskDateStr !== todayStr) return; // Skip tasks not for today

      task.assignedTeam.forEach((member) => {
        if (!stats[member]) {
          stats[member] = { completed: 0, incomplete: 0 };
        }

        if (task.completed) {
          stats[member].completed += 1;
        } else {
          stats[member].incomplete += 1;
        }
      });
    });

    return stats;
  };
  const renderProductivityView = () => {
    // Set which date(s) to use based on view mode
    let rangeDates = [];
    if (productivityMode === "Today") {
      rangeDates = [today];
    } else if (productivityMode === "Week") {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay()); // Sunday
      for (let i = 1; i <= 7; i++) {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        rangeDates.push(d);
      }
    } else if (productivityMode === "Month") {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
        rangeDates.push(new Date(d));
      }
    }

    // Helper to collect tasks in the date range
    const tasksInRange = tasks.filter((task) =>
      rangeDates.some((date) =>
        new Date(task.datetime).toDateString() === date.toDateString()
      )
    );

    // Overdue = not completed and its instant (deadline || datetime || date) is past, computed
    // in the user's STORED timezone via the shared helper — same as the badge and chat.
    // (Fixes the old deadline-only check and null-deadline → 1970 → always-overdue bug.)
    const overdueTasks = tasksInRange.filter((task) => isTaskOverdue(task, currentUser?.timezone));

    // To Do = not completed and not overdue (fix(task-dates): it was `deadline >= now`, so a task
    // with no deadline, new Date(null) = 1970, was never counted; now the three counts partition).
    const todoTasks = tasksInRange.filter((task) => isTaskToDo(task, currentUser?.timezone));

    // Completed = completed and within range
    const completedTasks = tasksInRange.filter((task) => task.completed);

    return (
      <div className="dashboard-box" style={{ marginBottom: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Productivity</h3>
          <select
            value={productivityMode}
            onChange={(e) => setProductivityMode(e.target.value)}
            style={{
              padding: "0.4rem 0.7rem",
              borderRadius: "6px",
              fontSize: "1rem",
              border: "1px solid #ccc",
              fontWeight: 600,
            }}
          >
            <option value="Today">Today</option>
            <option value="Week">Week</option>
            <option value="Month">Month</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: "1.5rem", marginTop: "1.2rem" }}>
          {/* Overdue */}
          <div style={{ flex: 1, textAlign: "center" }}>
            <h4 style={{ color: "#D7263D" }}>Overdue</h4>
            <div style={{ fontSize: "2.5rem", fontWeight: 700 }}>
              {overdueTasks.length}
            </div>
            <div style={{ color: "#888", fontSize: "1rem" }}>
              tasks
            </div>
          </div>
          {/* To Do */}
          <div style={{ flex: 1, textAlign: "center" }}>
            <h4>To Do</h4>
            <div style={{ fontSize: "2.5rem", fontWeight: 700 }}>
              {todoTasks.length}
            </div>
            <div style={{ color: "#888", fontSize: "1rem" }}>
              tasks
            </div>
          </div>
          {/* Completed */}
          <div style={{ flex: 1, textAlign: "center" }}>
            <h4 style={{ color: "#47A992" }}>Completed</h4>
            <div style={{ fontSize: "2.5rem", fontWeight: 700 }}>
              {completedTasks.length}
            </div>
            <div style={{ color: "#888", fontSize: "1rem" }}>
              tasks
            </div>
          </div>
        </div>
      </div>
    );
  };
  const sortDashboardTasks = (tasks) => {
    if (dashboardSortBy === "time") {
      return [...tasks].sort(
        (a, b) => new Date(a.deadline) - new Date(b.deadline)
      );
    } else {
      // Priority first, then by deadline time
      return [...tasks].sort((a, b) => {
        const priorityOrder =
          priorityRank[a.priority] - priorityRank[b.priority];
        if (priorityOrder !== 0) return priorityOrder;
        return new Date(a.deadline) - new Date(b.deadline);
      });
    }
  };
  function handleChatSubmit(e) {
    e.preventDefault();
    if (chatInput.trim()) {
      setChatMessages([...chatMessages, { role: "user", content: chatInput }]);
      setChatInput("");
      // Optionally, add a bot reply here
    }
  }

  function handleMoveTask(taskId, newTime) {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId
          ? { ...task, time: newTime }
          : task
      )
    );
    api(`/api/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify({ time: newTime }) })
      .catch(err => console.error('Failed to persist task move:', err));
  }

  function handleCancelTask(taskId) {
    setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
    api(`/api/tasks/${taskId}`, { method: 'DELETE' })
      .catch(err => console.error('Failed to persist task cancellation:', err));
  }

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('aidey_sidebar_collapsed', String(next));
      return next;
    });
  };

  const handleMenuItemClick = (view) => {
    setFilter(view);
    const path = VIEW_TO_PATH[view] || '/';
    window.history.pushState({}, '', path);
    // Decision 90: a page that reads its own address (Notes, open on a note) hears that it changed.
    window.dispatchEvent(new Event('aidey:pathchange'));
    closeMobileMenu();
  };

  // ── UI-4: the shell ───────────────────────────────────────────────────────────────────────
  // One way to go anywhere from the sidebar, the drawer and search: the Team items keep their
  // upgrade prompt, an item with a deep link (Personal's Calendar, decision 56) publishes it
  // first, and the drawer always closes (a Team upgrade prompt opened BEHIND the old menu).
  const [navId, setNavId] = useState(null);
  // UI-4b: folded sidebar groups, remembered per workspace in this browser (aidey_sidebar_groups:
  // { [workspaceId]: { [groupKey]: true } }). Storage can be missing (private window): the
  // sidebar then starts unfolded and the folds last for the visit.
  const [navFoldsAll, setNavFoldsAll] = useState(() => {
    try { return JSON.parse(localStorage.getItem('aidey_sidebar_groups') || '{}') || {}; } catch { return {}; }
  });
  const navFolds = navFoldsAll[activeWorkspaceId || 'none'] || {};
  // Decision 60: the user card. In a business they OWN (the workspace list marks the others isTeam or
  // isMember) the role is Owner; elsewhere it is their membership role (teamRole). Personal shows no
  // business role. The Admin tag is for platform admins only, beside the role, never instead of it
  // (2026-10-08: Carla, an owner and a platform admin, saw "Admin Admin").
  const shellUserName = (currentUser?.name || [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') || '').trim();
  const shellUser = {
    name: shellUserName,
    initials: shellUserName.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?',
    role: userCardRole({ workspace: activeWorkspace, teamRole: currentUser?.teamRole, isPersonal: isPersonalWorkspace }),
    admin: isAdminRole(currentUser?.role),
  };
  // The My tasks badge (fresh-eyes 15, 2026-10-12): the server's due today PLUS overdue, which never
  // overlap (GET /api/tasks/counts, lib/taskCounts), so every task to do by the end of today counts
  // once. The My tasks subtitle shows the same two numbers apart. It was counted here from the rows.
  const taskCounts = useTaskCounts(tasks, { enabled: !!currentUser?.id && !!activeWorkspaceId, workspaceId: activeWorkspaceId });
  const tasksDueCount = taskCounts.counts ? taskCounts.counts.dueToday + taskCounts.counts.overdue : 0;
  const toggleNavFold = (key) => setNavFoldsAll((all) => {
    const ws = activeWorkspaceId || 'none';
    const next = { ...all, [ws]: { ...(all[ws] || {}), [key]: !(all[ws] || {})[key] } };
    try { localStorage.setItem('aidey_sidebar_groups', JSON.stringify(next)); } catch { /* storage blocked */ }
    return next;
  });
  const shellNavigate = (item) => {
    const id = itemId(item);
    const { view, deep } = navTarget(id);
    closeMobileMenu();
    setNavId(id);
    if (deep) setDeepLink(deep.entity, deep.id);
    if (item.team) handleTeamNavClick(view); else handleMenuItemClick(view);
  };
  // The item to mark as current: the one chosen, while its view is showing; else the view.
  const activeNavId = navId && navTarget(navId).view === filter ? navId : filter;
  const openAddTask = () => {
    setEditingId(null);
    setTitle("");
    setDetails("");
    setPriority("Medium");
    setStartDate(null);
    setStartTime(null);
    setDeadlineDate(null);
    setDeadlineTime(null);
    setTaskType("");
    setAssignedTeam([]);
    setShowModal(true);
    setReminderOffset(5);
    setCustomReminder("");
    closeMobileMenu();
  };

  const openBillingSettings = () => {
    setSettingsInitialSection('billing');
    setFilter('Settings');
    closeMobileMenu();
  };

  const handleUpgradeRequest = (requestedPlan = 'pro') => {
    const normalizedPlan = (requestedPlan || 'pro').toString().toLowerCase();
    const planName = normalizedPlan === 'team' ? 'team' : 'pro';
    const quantity = planName === 'team' ? 2 : undefined;
    redirectToCheckout({
      planName,
      billingPeriod: 'monthly',
      quantity,
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
    closeMobileMenu();
  };

  function handleCreateProjectInvoice(projectId) {
    setProjectInvoiceTarget(projectId);
    setFilter('Invoices');
  }

  function handleBillTime(entries) {
    setBillTimeTarget(entries);
    setFilter('Invoices');
  }

  // Get current view title for mobile header
  const getCurrentViewTitle = () => {
    switch (filter) {
      case 'Dashboard': return 'Dashboard';
      case 'Tasks': return 'My Tasks';
      case 'TeamDashboard': return 'Team Dashboard';
      case 'TeamCalendar': return 'Team Calendar';
      case 'TeamHR': return 'HR';   // the fallback split it into "Team H R" (Block I)
      case 'Financials': return 'Financial Overview';
      case 'ProjectTracker': return 'Projects';
      case 'Notes': return 'Notes';
      case 'Calendar': return 'Calendar';
      case 'Notifications': return 'Notifications';
      case 'Today': return 'Today';
      case 'Tomorrow': return 'Tomorrow';
      case 'Week View': return 'Week View';
      case 'Contacts': return 'Contacts';
      case 'CRM': return 'CRM';
      case 'Forms': return 'Forms';
      case 'Workflows': return 'Workflows';
      case 'Proposals': return 'Proposals';
      case 'Contracts': return 'Contracts';
      case 'Scheduler': return 'Scheduler';
      case 'ClientPortal': return 'Client Portal';
      case 'EmailDashboard': return 'Emails';
      case 'Invoices': return 'Invoicing';
      case 'AideyChat': return 'Aidey';
      case 'Timesheet': return 'Timesheet';
      case 'Insights': return 'Insights';
      case 'Shopping': return 'Shopping';
      case 'MealPlanner': return 'Meal Planner';
      case 'Settings': return 'Settings';
      case 'AdminPanel': return 'Admin Panel';
      case 'UiKit': return 'UI kit';
      case 'NotFound': return 'Page not found';
      case 'Profile': return 'Account';
      default: return String(filter || 'Aidey').replace(/([A-Z])/g, ' $1').trim();
    }
  };

  // While restoreSession is running we don't know yet whether the user is
  // logged in — show a neutral splash so they are never incorrectly bounced
  // to the login page on refresh or back-navigation.
  if (authLoading) return <BootSplash />;

  // Network failure during boot — show a retry card rather than the login page,
  // since the user's session may still be valid and the server may just be
  // temporarily unreachable.
  if (authConnectionFailed && !currentUser) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(150deg, #0C2D48 0%, #1a4a7a 50%, #0a2038 100%)',
      }}>
        <div style={{ textAlign: 'center', fontFamily: 'Raleway, sans-serif' }}>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, marginBottom: 8 }}>
            Can't reach Aidey, check your connection.
          </p>
          <button
            onClick={retryConnection}
            style={{
              marginTop: 8, padding: '10px 24px', borderRadius: 8, border: 'none',
              background: '#E4E69B', color: '#0C2D48', fontWeight: 600,
              fontSize: 14, cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ACCOUNT-4 paywall — driven SOLELY by the backend enforcing verdict, the same
  // decision the API gates on (hasAccess === false ⇒ every non-exempt call 402s).
  // Fail-open: if hasAccess is absent (older cached user), don't lock anyone out.
  //
  // CASCADE-2: a blocked member is NOT always the person who can pay. Route by who
  // they are — a CHILD sees a child-friendly screen, a covered ADULT (business or
  // household) sees "ask the owner" with no payment action, and only an actual
  // OWNER (not covered, not a child) sees the pay-now paywall.
  // Card at sign-up (phase 2): a new account that has not started its trial opens only here
  // (lib/cardAtSignup decides; the API answers everything else 402 CARD_SETUP_REQUIRED).
  if (currentUser && currentUser.cardSetupRequired === true) {
    return (
      <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
        <StartTrialScreen />
      </React.Suspense>
    );
  }
  if (currentUser && currentUser.hasAccess === false) {
    if (currentUser.isChildAccount) {
      return <ChildPaused />;
    }
    if (currentUser.blockReason === 'owner_lapsed' || currentUser.isCoveredMember) {
      return <CoveredMemberPaused ownerName={currentUser.accountOwnerName} />;
    }
    return <TrialPaywall currentUser={currentUser} />;
  }

  return (
    <>
      {!currentUser ? (
        <Login />
      ) : (
        <div className="app-container">
          <TrialBanner setFilter={setFilter} onUpgrade={() => openBillingSettings()} />
          <RemovedMemberBanner onChoosePlan={() => openBillingSettings()} />
          {workspacesLoadError && (
            <div style={{ background: '#fef9c3', borderBottom: '1px solid #fbbf24', color: '#92400e', padding: '0.45rem 1rem', fontSize: '0.82rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Workspace list could not be loaded. Using default workspace.</span>
            </div>
          )}
          {subscriptionCheckPending && (
            <div style={{ background: '#eff6ff', borderBottom: '1px solid #93c5fd', color: '#1e40af', padding: '0.45rem 1rem', fontSize: '0.82rem', textAlign: 'center' }}>
              Finishing setup… please wait a moment.
            </div>
          )}

          <div className="ui-shell">
          {/* ── UI-4: THE SHELL. One Sidebar: the desktop sidebar (or the tablet's rail) here, and
              the same component inside the phone drawer below, so the two can never list
              different things again. The phone gets the top bar. ── */}
          {(() => {
            const shellWho = { isPersonal: isPersonalWorkspace, hasTeamPlanAccess, userIsTeamAdmin, role: currentUser?.role, canSeeLedger: !!currentUser?.canSeeLedger };
            const shellNav = {
              folded: navFolds, onFold: toggleNavFold, user: shellUser, tasksDueCount,
              groups: sidebarGroups(shellWho), footer: sidebarFooter(shellWho), activeId: activeNavId, t,
              onSelect: shellNavigate, onAsk: () => handleMenuItemClick('AideyChat'), onAddTask: openAddTask,
              onSearch: () => { closeMobileMenu(); setShowSearch(true); }, onLogout: handleLogout,
              emailUnreadCount, hasProPlanAccess, hasTeamPlanAccess,
            };
            return (
              <>
                <Sidebar variant={shellBp === 'tablet' ? 'rail' : 'desk'} collapsed={sidebarCollapsed} {...shellNav}
                  onToggle={shellBp === 'tablet' ? () => setIsMobileMenuOpen(true) : toggleSidebar}
                  bell={<NotificationBell variant="sidebar" setFilter={setFilter} extraCount={attentionCount} />}
                  workspace={<WorkspaceSwitcher />} />
                <Drawer open={isMobileMenuOpen} onClose={closeMobileMenu}>
                  <Sidebar variant="drawer" {...shellNav} onClose={closeMobileMenu}
                    bell={<NotificationBell variant="sidebar" setFilter={setFilter} extraCount={attentionCount} />}
                    workspace={<WorkspaceSwitcher mobile />}
                    buildSha={((process.env.REACT_APP_BUILD_SHA || '').slice(0, 7)) || 'dev'} />
                </Drawer>
              </>
            );
          })()}

          <div className="ui-shell__main">
          <TopBar title={getCurrentViewTitle()} onMenu={toggleMobileMenu} menuOpen={isMobileMenuOpen}
            bell={<NotificationBell variant="topbar" setFilter={setFilter} extraCount={attentionCount} />} />

          <ErrorBoundary>
          <main className="main-content">
            {currentUser?.showSoloProOffer && <SoloProOffer />}
            {/* Fresh-eyes item 27: an address that is no page says so, with the way to the Dashboard. */}
            {filter === "NotFound" && (
              <NotFoundPage onHome={() => handleMenuItemClick('Dashboard')} />
            )}
            {filter === "Dashboard" && (
              <Dashboard
                tasksLoadFailed={tasksLoadFailed}
                today={today}
                dashboardSortBy={dashboardSortBy}
                setDashboardSortBy={setDashboardSortBy}
                getTodayTasks={getTodayTasks}
                setViewTask={setViewTask}
                taskTypes={activeTaskTypes}
                quickNotes={quickNotes}
                newNote={newNote}
                setNewNote={setNewNote}
                addNote={addNote}
                deleteNote={deleteNote}
                setViewNote={setViewNote}
                renderProductivityView={renderProductivityView}
                setFilter={setFilter}
                chatLoading={chatLoading}
                setChatLoading={setChatLoading}
                handleAddTaskFromChat={handleAddTaskFromChat}
                handleAideyEditTask={handleAideyEditTask}
                handleAideyDeleteTask={handleAideyDeleteTask}
                tasks={tasks}
                clashInfo={clashInfo}
                noteCategories={noteCategories}
                setNoteCategories={setNoteCategories}
                markComplete={markComplete}
                onAddTask={openAddTaskModal}   // Block B: the header's "+ Add task" (mockup)
              />
            )}

            {filter === "ProjectTracker" && (
              <PlanGate requiredPlan="pro" featureName="Projects" featureDescription="Track projects, sprints, tasks, and budgets with full project management tools." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                {/* Create invoice opens Invoices, a Business page: not offered in Personal. */}
                <ProjectTracker onCreateProjectInvoice={isPersonalWorkspace ? undefined : handleCreateProjectInvoice} onNavigate={setFilter} />
              </PlanGate>
            )}

            {filter === "Tasks" && (
              <TasksPage
                displayTasks={displayTasks}
                tasksLoadFailed={tasksLoadFailed}
                taskCounts={taskCounts}
                today={today}
                tomorrow={tomorrow}
                sortBy={sortBy}
                setSortBy={setSortBy}
                onViewTask={setViewTask}
                taskTypes={activeTaskTypes}
                undoComplete={undoComplete}
                onMoveTask={moveTask}
                onAddTask={openAddTaskModal}
                currentUser={currentUser}
                setSelectedWeekDate={setSelectedWeekDate}
                setShowWeekModal={setShowWeekModal}
                calendarDate={calendarDate}
                setCalendarDate={setCalendarDate}
                setSelectedDate={setSelectedDate}
                setShowCalendarModal={setShowCalendarModal}
                tileContent={tileContent}
                initialTab={calInitialTab}
              />
            )}

            {filter === "Today" && (
              <TaskList
                tasksLoadFailed={tasksLoadFailed}
                tasks={displayTasks}
                mode="Today"
                sortBy={sortBy}
                setSortBy={setSortBy}
                onViewTask={setViewTask}
                today={today}
                tomorrow={tomorrow}
                taskTypes={activeTaskTypes}
                undoComplete={undoComplete}
                onMoveTask={moveTask}
                onAddTask={openAddTaskModal}
                currentUser={currentUser}
              />
            )}
            {filter === "Tomorrow" && (
              <TaskList
                tasksLoadFailed={tasksLoadFailed}
                tasks={displayTasks}
                mode="Tomorrow"
                sortBy={sortBy}
                setSortBy={setSortBy}
                onViewTask={setViewTask}
                today={today}
                tomorrow={tomorrow}
                taskTypes={activeTaskTypes}
                undoComplete={undoComplete}
                onMoveTask={moveTask}
                onAddTask={openAddTaskModal}
                currentUser={currentUser}
              />
            )}
            {filter === "Week View" && (
              <WeekView
                tasks={displayTasks}
                today={today}
                setSelectedWeekDate={setSelectedWeekDate}
                setShowWeekModal={setShowWeekModal}
                setViewTask={setViewTask}
                onNavigateDay={(date) => {
                  const todayStr = today.toDateString();
                  const tomorrowStr = tomorrow.toDateString();
                  if (date.toDateString() === todayStr) {
                    handleMenuItemClick("Today");
                  } else if (date.toDateString() === tomorrowStr) {
                    handleMenuItemClick("Tomorrow");
                  } else {
                    setSelectedWeekDate(date);
                    setShowWeekModal(true);
                  }
                }}
                onAddTask={openAddTaskModal}
              />
            )}
            {filter === "Notes" && (
              <Notes
                quickNotes={quickNotes}
                setQuickNotes={setQuickNotes}
                newNote={newNote}
                setNewNote={setNewNote}
                searchNote={searchNote}
                setSearchNote={setSearchNote}
                addNote={addNote}
                deleteNote={deleteNote}
                setViewNote={setViewNote}
                viewNote={viewNote}
                formatNoteDate={formatNoteDate}
                noteCategories={noteCategories}
                setNoteCategories={setNoteCategories}
              />
            )}
            {filter === "Profile" && (
              <Profile />
            )}
            {filter === "EmailDashboard" && (
              <EmailInbox setFilter={handleMenuItemClick} />
            )}
            {filter === "TeamCalendar" && (
              <PlanGate requiredPlan="team" featureName="Team Calendar" featureDescription="View and coordinate your team's schedules, time-off, and availability in one shared calendar." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <TeamCalendar
                  userSchedule={tasks}
                  onEditTask={handleAideyEditTask}
                  onDeleteTask={handleAideyDeleteTask}
                  onAddTask={(date) => {
                    setEditingId(null);
                    setTitle("");
                    setDetails("");
                    setPriority("Medium");
                    // UI-17: a tapped empty slot passes its day; the header button passes nothing.
                    setStartDate(date instanceof Date ? date : null);
                    setStartTime(null);
                    setDeadlineDate(null);
                    setDeadlineTime(null);
                    setTaskType("");
                    setAssignedTeam([]);
                    setReminderOffset(5);
                    setCustomReminder("");
                    setShowModal(true);
                  }}
                />
              </PlanGate>
            )}
            {filter === "TeamManagement" && (
              <PlanGate requiredPlan="team" featureName="Team Management" featureDescription="Invite team members, assign roles, manage leave allowances, and control workspace access." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <TeamManagement />
              </PlanGate>
            )}
            {filter === "TeamDashboard" && (hasTeamPlanAccess && !userIsTeamAdmin ? (
              <div style={{ padding: "3rem 2rem", textAlign: "center", color: "#888" }}>
                <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>🔒</div>
                <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Access restricted</div>
                <div style={{ fontSize: "0.9rem" }}>The Team Dashboard is available to team administrators only.</div>
              </div>
            ) : (
              <PlanGate requiredPlan="team" featureName="Team Dashboard" featureDescription="See your whole team's task progress, workload, and performance in one shared view." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <TeamDashboard onAddTask={openAddTaskModal} />
              </PlanGate>
            ))}
            {filter === "TeamHR" && (hasTeamPlanAccess && !userIsTeamAdmin ? (
              <div style={{ padding: "3rem 2rem", textAlign: "center", color: "#888" }}>
                <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>🔒</div>
                <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Access restricted</div>
                <div style={{ fontSize: "0.9rem" }}>HR Management is available to team administrators only.</div>
              </div>
            ) : (
              <PlanGate requiredPlan="team" featureName="HR Management" featureDescription="Manage employee profiles, documents, onboarding, performance reviews, training, leave, and overtime." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <Suspense fallback={<div style={{ padding: "2rem", color: "#888" }}>Loading HR…</div>}>
                  <HROverview />
                </Suspense>
              </PlanGate>
            ))}
            {filter === "List" && (
              <TaskList
                tasksLoadFailed={tasksLoadFailed}
                tasks={displayTasks}
                mode="List"
                sortBy={sortBy}
                setSortBy={setSortBy}
                onViewTask={setViewTask}
                today={today}
                tomorrow={tomorrow}
                taskTypes={activeTaskTypes}
                undoComplete={undoComplete}
                onMoveTask={moveTask}
                onAddTask={openAddTaskModal}
                currentUser={currentUser}
              />
            )}
            {filter === "Contacts" && (
              <ContactsDashboard isPersonal={isPersonalWorkspace} />
            )}
            {filter === "CRM" && (
              <PlanGate requiredPlan="pro" featureName="CRM Pipeline" featureDescription="Manage leads, deals, and customer relationships with a visual Kanban pipeline." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <CRMDashboard currentUser={currentUser} onNavigateUpgrade={handleUpgradeRequest} />
              </PlanGate>
            )}
            {filter === "Forms" && (
              <PlanGate requiredPlan="pro" featureName="Forms" featureDescription="Build lead capture forms, intake questionnaires, and client onboarding flows." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <React.Suspense fallback={<div style={{ padding: 40, color: '#888' }}>Loading…</div>}>
                  <FormsPage onUpgrade={() => handleUpgradeRequest('pro')} />
                </React.Suspense>
              </PlanGate>
            )}
            {filter === "Workflows" && (
              <PlanGate requiredPlan="pro" featureName="Workflows" featureDescription="Automate your client journey with drag-and-drop workflow automation triggered by forms, proposals, bookings, and more." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <React.Suspense fallback={<div style={{ padding: 40, color: '#888' }}>Loading…</div>}>
                  <WorkflowsDashboard onUpgrade={() => handleUpgradeRequest('pro')} />
                </React.Suspense>
              </PlanGate>
            )}
            {filter === "Proposals" && (
              <PlanGate requiredPlan="pro" featureName="Proposals" featureDescription="Create and send branded proposals with e-signature and acceptance tracking." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <React.Suspense fallback={<div style={{ padding: 40, color: '#888' }}>Loading…</div>}>
                  <ProposalsDashboard onUpgrade={() => handleUpgradeRequest('pro')} />
                </React.Suspense>
              </PlanGate>
            )}
            {filter === "Contracts" && (
              <PlanGate requiredPlan="pro" featureName="Contracts" featureDescription="Draft, send, and collect signatures on legally binding contracts." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <React.Suspense fallback={<div style={{ padding: 40, color: '#888' }}>Loading…</div>}>
                  <ContractsDashboard onUpgrade={() => handleUpgradeRequest('pro')} />
                </React.Suspense>
              </PlanGate>
            )}
            {filter === "Scheduler" && (
              <PlanGate requiredPlan="pro" featureName="Booking Pages" featureDescription="Let clients book meetings directly into your calendar with custom booking pages." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <React.Suspense fallback={<div style={{ padding: 40, color: '#888' }}>Loading…</div>}>
                  <SchedulerDashboard onUpgrade={() => handleUpgradeRequest('pro')} />
                </React.Suspense>
              </PlanGate>
            )}
            {filter === "ClientPortal" && (
              <PlanGate requiredPlan="pro" featureName="Client Portals" featureDescription="Give clients a branded portal to view documents, messages, and project updates." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <React.Suspense fallback={<div style={{ padding: 40, color: '#888' }}>Loading…</div>}>
                  <PortalDashboard onUpgrade={() => handleUpgradeRequest('pro')} />
                </React.Suspense>
              </PlanGate>
            )}
            {filter === "Invoices" && (
              hasBasicsPlanAccess ? (
                <InvoicingLayout
                  externalOpenFormWithProjectId={projectInvoiceTarget}
                  onExternalFormOpened={() => setProjectInvoiceTarget(null)}
                  externalBillTimeEntries={billTimeTarget}
                  onBillTimeOpened={() => setBillTimeTarget(null)}
                />
              ) : (
                <BasicFinancialSummary setFilter={setFilter} onUpgrade={handleUpgradeRequest} />
              )
            )}
            {/* Decision 70: the ledger rule (owner or finance), the same answer the sidebar uses. */}
            {filter === "Financials" && (!currentUser?.canSeeLedger ? (
              <div style={{ padding: "3rem 2rem", textAlign: "center", color: "var(--ink-2)" }}>
                <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>🔒</div>
                <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Access restricted</div>
                <div style={{ fontSize: "var(--text-secondary)" }}>Financial overview is for the business owner and the finance role.</div>
              </div>
            ) : hasProPlanAccess ? (
              <FinancialDashboard onNavigate={handleMenuItemClick} />
            ) : (
              <BasicFinancialSummary readOnly invoicesIncluded={hasBasicsPlanAccess} setFilter={setFilter} onUpgrade={handleUpgradeRequest} />
            ))}
            {filter === "AideyChat" && (
              <AideyChatDashboard
                onCreateTask={handleAddTaskFromChat}
                userSchedule={tasks}
                onMoveTask={chatTaskActions.moveTask}
                onCancelTask={chatTaskActions.cancelTask}
                activeWorkspaceId={activeWorkspaceId}
                onClose={() => handleMenuItemClick(pageBeforeChat.current)}   // the view AND its address, so a reload stays closed
              />
            )}
            {/* Block K: the same gate (isAdminRole: super_admin, admin, cs_manager), held in AdminPanelRoute; anyone else is told, not shown a blank. */}
            {filter === "AdminPanel" && <AdminPanelRoute currentUser={currentUser} />}
            {/* UI-2: the UI kit. The Admin panel's role check; anyone else is told, not shown a blank. */}
            {filter === "UiKit" && currentUser && (isAdminRole(currentUser.role) ? (
              <React.Suspense fallback={<div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading…</div>}>
                <UiKitPage />
              </React.Suspense>
            ) : (
              <div data-testid="ui-kit-refused" style={{ padding: 40, fontFamily: 'var(--font-body)', color: 'var(--ink)' }}>
                This page is for Aidey admins.
              </div>
            ))}
            {filter === "Timesheet" && (
              <PlanGate requiredPlan="pro" featureName="Time Tracking" featureDescription="Log billable hours, track time against projects, and export timesheets for invoicing." setFilter={setFilter} onUpgrade={handleUpgradeRequest}>
                <React.Suspense fallback={<div style={{ padding: 40, color: '#888' }}>Loading…</div>}>
                  <TimesheetView onUpgrade={() => handleUpgradeRequest('pro')} onBillTime={handleBillTime} />
                </React.Suspense>
              </PlanGate>
            )}
            {filter === "Insights" && (
              <React.Suspense fallback={<div style={{ padding: 40, color: '#888' }}>Loading…</div>}>
                <InsightsPage handleAddTask={handleAddTaskFromChat} onNavigate={handleMenuItemClick} />
              </React.Suspense>
            )}
            {filter === "Settings" && (
              <React.Suspense fallback={<div style={{ padding: 40, color: '#fff' }}>Loading…</div>}>
                <SettingsLayout
                  setFilter={setFilter}
                  initialSection={settingsInitialSection}
                />
              </React.Suspense>
            )}
            {filter === "Notifications" && (
              <NotificationsPage setFilter={setFilter} />
            )}
            {filter === "Calendar" && !isPersonalWorkspace && (
              <CalendarView
                asPage
                calendarDate={calendarDate}
                setCalendarDate={setCalendarDate}
                setSelectedDate={setSelectedDate}
                setShowCalendarModal={setShowCalendarModal}
                tileContent={tileContent}
                tasks={displayTasks}
              />
            )}
            {filter === "Shopping" && isPersonalWorkspace && (
              <ShoppingList />
            )}
            {filter === "Renewals" && isPersonalWorkspace && (
              <Renewals defaultTab="renewals" />
            )}
            {filter === "Birthdays" && isPersonalWorkspace && (
              <Renewals defaultTab="birthdays" />
            )}
            {filter === "Journal" && isPersonalWorkspace && (
              <Journal />
            )}
            {filter === "Family" && isPersonalWorkspace && (
              <Family
                googleEvents={googleEvents}
                icalEvents={icalEvents}
                outlookEvents={outlookEvents}
              />
            )}
            {filter === "FamilyChat" && isPersonalWorkspace && (
              <FamilyChat />
            )}
            {filter === "Budget" && isPersonalWorkspace && (
              <Budget />
            )}
            {filter === "MealPlanner" && isPersonalWorkspace && (
              <MealPlanner />
            )}

            {/* Task Modal for Add Task */}
            {showModal && (
              <TaskModal
                show={showModal}
                onClose={() => {
                  setTaskFormClash(null);
                  setShowModal(false);
                  setEditingId(null);
                  setTitle("");
                  setDetails("");
                  setPriority("Medium");
                  setStartDate(null);
                  setStartTime(null);
                  setDeadlineDate(null);
                  setDeadlineTime(null);
                  setTaskType("");
                  setAssignedTeam([]);
                  setHouseholdMemberId(null);
                  setReminderOffset(5);
                  setBusy(true);
                  setIsPersonal(false);
                  setRecurrence(null);
                  setTaskProject(null);
                }}
                clashWarning={taskFormClash}
                onSaveAnyway={handleSaveAnyway}
                onChangeTime={handleChangeTime}
                title={title}
                setTitle={setTitle}
                details={details}
                setDetails={setDetails}
                priority={priority}
                setPriority={setPriority}
                startDate={startDate}
                setStartDate={setStartDate}
                startTime={startTime}
                setStartTime={setStartTime}
                deadlineDate={deadlineDate}
                setDeadlineDate={setDeadlineDate}
                deadlineTime={deadlineTime}
                setDeadlineTime={setDeadlineTime}
                taskType={taskType}
                setTaskType={setTaskType}
                assignedTeam={assignedTeam}
                setAssignedTeam={setAssignedTeam}
                householdMemberId={householdMemberId}
                setHouseholdMemberId={setHouseholdMemberId}
                reminderOffset={reminderOffset}
                setReminderOffset={setReminderOffset}
                busy={busy}
                setBusy={setBusy}
                isPersonal={isPersonal}
                setIsPersonal={setIsPersonal}
                meetingType={meetingType}
                setMeetingType={setMeetingType}
                location={location}
                setLocation={setLocation}
                recurrence={recurrence}
                setRecurrence={setRecurrence}
                project={taskProject}
                setProject={setTaskProject}
                editingId={editingId}
                onSubmit={handleAddTaskForm}
                taskTypes={activeTaskTypes}
              />
            )}

            {/* Decision 65: which occurrences of a repeating task this edit changes. */}
            <RecurringEditDialog
              open={!!seriesPrompt}
              // UI-24 item 25: Repeat turned off in the form (or in the form held by the clash warning),
              // for an account the server has it on for (currentUser.features); otherwise main's question.
              repeatOff={repeatOffKeepsOccurrence(currentUser) && !!editingOccurrence && !(seriesPrompt?.fromClash ? taskFormClash?.taskData?.recurrence : recurrence)}
              onClose={() => setSeriesPrompt(null)}
              onChoose={(scope) => {
                const fromClash = seriesPrompt?.fromClash;
                setSeriesPrompt(null);
                if (fromClash) handleSaveAnyway(scope);
                else handleAddTaskForm({ preventDefault() {}, _seriesScope: scope });
              }}
            />

            {/* View Task Modal */}
            {viewTask && (
              <div className="modal-overlay">
                <div className={`modal-content ${(viewTask.priority || 'medium').toLowerCase()}`}>
                  <button
                    className="close-button"
                    onClick={() => setViewTask(null)}
                  >
                    ✕
                  </button>
                  {/* Task Title & Type */}
                  <h3 style={{ marginBottom: "0.03rem", fontSize: "1.5rem", fontWeight: 700 }}>
                    {viewTask.title}
                    {viewTask.taskType && (
                      <span
                        style={{
                          fontSize: "1.15rem",
                          display: "block",
                          color: "#666",
                          marginTop: "0.03rem",
                          fontWeight: 500,
                        }}
                      >
                        {taskTypes.find((t) => t.value === viewTask.taskType)?.label}
                      </span>
                    )}
                  </h3>

                  {/* calendar-2: a change a calendar did not take, with Retry (nothing when none) */}
                  <CalendarSyncNotice task={viewTask} />

                  {/* Quick time-log prompt — Pro only, dismissible per task */}
                  <React.Suspense fallback={null}>
                    <TaskTimePrompt
                      task={viewTask}
                      isPro={userIsPro}
                      onSaved={() => { }}
                    />
                  </React.Suspense>

                  <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "0.7rem 0" }} />

                  {/* Task Details Heading */}
                  <div style={{ fontWeight: "bold", margin: "0.08rem 0 0.01rem 0", fontSize: "1.08rem" }}>
                    Task Details
                  </div>
                  {/* Date and Time */}
                  <div style={{ marginBottom: viewTask.details ? "0.08rem" : "0.04rem", fontSize: "1.18rem" }}>
                    {(() => {
                      // fix(task-dates): the shared formatters. A date-only deadline showed as ending at
                      // 01:00 (midnight UTC); it now shows as a date.
                      const dateLine = formatTaskDateLine(viewTask);
                      if (!dateLine) return <div style={{ color: '#888' }}>No date set</div>;
                      const timeLine = formatTaskTimeRange(viewTask);
                      return (
                        <>
                          <div style={{ fontWeight: 500, marginBottom: "0.18rem" }} data-testid="view-task-date">{dateLine}</div>
                          {timeLine && <div style={{ color: "#555" }} data-testid="view-task-time">{timeLine}</div>}
                        </>
                      );
                    })()}
                  </div>

                  <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "0.7rem 0" }} />

                  {/* Task Information Heading and Details */}
                  {viewTask.details && (
                    <>
                      <div style={{
                        fontWeight: "bold",
                        margin: "0.08rem 0 0.01rem 0",
                        fontSize: "1.08rem"
                      }}>
                        Task Information
                      </div>
                      <div style={{ color: "#444", fontSize: "1.18rem", marginBottom: "0.06rem", whiteSpace: "pre-line" }}>
                        {/* D27: preserve line breaks (one answer per line) + strip the internal "[Form] " tag */}
                        {(viewTask.details || "").replace(/\[Form\]\s*/g, "")}
                      </div>
                    </>
                  )}

                  {/* Meeting Location */}
                  {viewTask.location && (() => {
                    // D27: three-way — a URL is a link, an Address is a map pin, and anything else
                    // (e.g. "Video call" with no join link configured) is plain text. Never link a
                    // non-address to Google Maps.
                    const loc = viewTask.location || "";
                    const isUrl = /^https?:\/\//i.test(loc);
                    const isAddress = viewTask.meetingType === "Address" || (!viewTask.meetingType && !isUrl);
                    return (
                      <>
                        <div style={{
                          fontWeight: "bold",
                          margin: "0.8rem 0 0.3rem 0",
                          fontSize: "1.08rem"
                        }}>
                          {isUrl ? "🔗 Meeting Link" : "📍 Location"}
                        </div>
                        <div style={{ color: "#444", fontSize: "1.18rem", marginBottom: "0.5rem" }}>
                          {isUrl ? (
                            <a
                              href={loc}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#2d81f7", textDecoration: "underline" }}
                            >
                              {loc}
                            </a>
                          ) : isAddress ? (
                            <a
                              href={`https://maps.google.com/?q=${encodeURIComponent(loc)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#2d81f7", textDecoration: "underline" }}
                            >
                              {loc}
                            </a>
                          ) : (
                            <span>{loc}</span>
                          )}
                        </div>
                      </>
                    );
                  })()}

                  {/* Recurrence Information */}
                  {viewTask.recurrence && (
                    <>
                      <div style={{
                        fontWeight: "bold",
                        margin: "0.8rem 0 0.3rem 0",
                        fontSize: "1.08rem"
                      }}>
                        Recurrence
                      </div>
                      <div style={{ color: "#444", fontSize: "1.18rem", marginBottom: "0.5rem" }}>
                        {String(viewTask.recurrence.type || '').toLowerCase() === "daily" &&
                          "🔄 Repeats daily"
                        }
                        {String(viewTask.recurrence.type || '').toLowerCase() === "weekly" &&
                          `🔄 Repeats every ${(viewTask.recurrence.day || 'week').charAt(0).toUpperCase() + (viewTask.recurrence.day || 'week').slice(1)}`
                        }
                        {String(viewTask.recurrence.type || '').toLowerCase() === "monthly" &&
                          `🔄 Repeats on the ${(viewTask.recurrence.date || 1)}${(viewTask.recurrence.date || 1) === 1 ? "st" :
                            (viewTask.recurrence.date || 1) === 2 ? "nd" :
                              (viewTask.recurrence.date || 1) === 3 ? "rd" :
                                "th"
                          } of each month`
                        }
                      </div>
                    </>
                  )}

                  {/* 2026-10-21: the due date's history (moves, reasons, who), newest first */}
                  <TaskDueHistory task={viewTask} />

                  {/* Attachments */}
                  <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "0.7rem 0" }} />
                  <div style={{ fontWeight: "bold", margin: "0.08rem 0 0.4rem 0", fontSize: "1.08rem" }}>Attachments</div>
                  {viewTaskAttachments.length === 0 ? (
                    <p style={{ color: '#aaa', fontSize: 13, margin: '0 0 0.5rem' }}>No attachments.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '0.5rem' }}>
                      {viewTaskAttachments.map(a => (
                        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 7, border: '1px solid #e0e3e8', background: '#fff' }}>
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.fileName}</span>
                          <a href={a.fileUrl || a.externalUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#0C2D48', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>Download</a>
                          <button onClick={() => handleDeleteViewTaskAttachment(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 18, lineHeight: 1, padding: 2 }} title="Remove">×</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="task-actions-group">
                    <button onClick={() => markComplete(viewTask.id)} className="complete-button">Complete</button>
                    <button onClick={() => handleEdit(viewTask)} className="neutral-button">Edit</button>
                    <button onClick={() => setTaskDeleteConfirm({ id: viewTask.id, name: viewTask.title, occurrence: occurrenceOf(viewTask) })} className="danger-button">Delete</button>
                  </div>
                </div>
              </div>
            )}

            {/* Calendar Day Sheet */}
            {showCalendarModal && selectedDate && (() => {
              const dayTasks = sortTasks(
                displayTasks.filter(t => {
                  if (t.completed) return false;
                  const taskDateStr = t.date || (t.datetime ? toLocalDateStr(new Date(t.datetime)) : null);
                  return taskDateStr === toLocalDateStr(selectedDate);
                })
              );
              const topTasks = dayTasks.slice(0, 4);
              const overflowCount = Math.max(0, dayTasks.length - 4);
              const dateHeading = selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
              const PRIORITY_DOT = { High: '#f59e0b', Medium: '#FBC490', Low: '#22c55e' };
              return (
                <div className="cal-day-overlay" onClick={e => { if (e.target === e.currentTarget) setShowCalendarModal(false); }}>
                  <div className="cal-day-sheet">
                    <button onClick={() => setShowCalendarModal(false)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
                    <h3 style={{ margin: '0 0 1rem', fontSize: '1.05rem', fontWeight: 800, color: '#0C2D48' }}>{dateHeading}</h3>

                    {dayTasks.length === 0 ? (
                      <p style={{ color: '#aaa', fontSize: '0.9rem', margin: '0 0 1rem' }}>No tasks for this day.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        {topTasks.map(task => (
                          <button key={task.id}
                            onClick={() => { setViewTask(task); setShowCalendarModal(false); }}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.75rem', border: '1px solid #e8eaef', borderRadius: 10, background: '#fafbff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', width: '100%' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: PRIORITY_DOT[task.priority] || '#a0aec0', flexShrink: 0 }} />
                            <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: '#1a202c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</span>
                            {task.time && <span style={{ fontSize: '0.75rem', color: '#718096', whiteSpace: 'nowrap' }}>{task.time}</span>}
                          </button>
                        ))}
                        {overflowCount > 0 && (
                          <button onClick={() => { setShowCalendarModal(false); setSelectedWeekDate(selectedDate); setShowWeekModal(true); }}
                            style={{ background: 'none', border: 'none', color: '#5352ed', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', textAlign: 'left', padding: '0.2rem 0', fontFamily: 'inherit' }}>
                            +{overflowCount} more, see all
                          </button>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => {
                        setShowCalendarModal(false);
                        setEditingId(null);
                        setTitle('');
                        setDetails('');
                        setPriority('Medium');
                        setStartDate(selectedDate);
                        setStartTime(null);
                        setDeadlineDate(null);
                        setDeadlineTime(null);
                        setTaskType('');
                        setAssignedTeam([]);
                        setReminderOffset(5);
                        setCustomReminder('');
                        setTaskProject(null);
                        setShowModal(true);
                      }}
                      type="button" className="ui-btn ui-btn--create ui-btn--block ui-addtask">
                      + Add task for this day
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Week View Task List Modal */}
            {showWeekModal && selectedWeekDate && (
              <div className="modal-overlay">
                <div className="modal-content neutral">
                  <button
                    className="close-button"
                    onClick={() => {
                      setShowWeekModal(false);
                      setSelectedWeekDate(null);
                    }}
                  >
                    ✕
                  </button>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      marginBottom: "1rem",
                    }}
                  >
                    Tasks for {selectedWeekDate.toDateString()}
                  </h3>
                  {tasksForDate(selectedWeekDate, displayTasks).length === 0 ? (
                    <p>No tasks for this day.</p>
                  ) : (
                    <ul style={{ listStyle: "none", padding: 0 }}>
                      {sortByTimeOnly(tasksForDate(selectedWeekDate, displayTasks)).map(
                        (task) => (
                          <li
                            key={task.id}
                            className={`task-card ${(task.priority || 'medium').toLowerCase()}`}
                            onClick={() => {
                              setViewTask(task);
                              setShowWeekModal(false);
                            }}
                            style={{
                              cursor: "pointer",
                              marginBottom: "0.5rem",
                              padding: "0.75rem 1rem",
                            }}
                          >
                            <strong>
                              {task.title}
                              {task.taskType && (
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    display: "block",
                                    color: "#666",
                                  }}
                                >
                                  {
                                    taskTypes.find((t) => t.value === task.taskType)
                                      ?.label
                                  }
                                </span>
                              )}
                            </strong>

                            <br />
                            <span style={{ fontSize: "0.85rem", color: "#666" }}>
                              {formatTaskTimeRange(task) /* fix(task-time): no end time from an empty deadline */}
                            </span>
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </div>
              </div>
            )}
            {/* View Note Modal (NEW!) */}
            {viewNote && (
              <div className="modal-overlay">
                <div className="modal-content neutral">
                  <button
                    className="close-button"
                    onClick={() => setViewNote(null)}
                  >
                    ✕
                  </button>
                  <h3 style={{ marginBottom: "1.2rem" }}>
                    📝 Note
                  </h3>
                  <div style={{ fontSize: "1.1rem", marginBottom: "1rem" }}>
                    {viewNote.text}
                  </div>
                  <div style={{ color: "#888", fontSize: "0.95rem", marginBottom: "1.5rem" }}>
                    Added: {formatNoteDate(viewNote.created)}
                  </div>
                  <button
                    onClick={() => {
                      deleteNote(viewNote.id);
                      setViewNote(null);
                    }}
                    style={{
                      padding: "0.5rem 1rem",
                      backgroundColor: "#E96A6A",
                      border: "none",
                      borderRadius: "6px",
                      color: "#fff",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Delete Note
                  </button>
                </div>
              </div>
            )}

            {/* UI-4: the floating Ask button (FloatingAsk). It opens the existing chat through
                'aidey:open-chat', hides while the chat, the drawer, the bell panel or any overlay
                is open and on the Aidey Chat page, keeps the tour's data-tour-id, and never opens
                the chat by itself (decision 40). */}
            <FloatingAsk hidden={filter === 'AideyChat'} />

            {/* Floating Aidey Chat Popup */}
            {showAideyChat && (
              <AideyPopup
                pageName={getCurrentViewTitle()}
                minimised={isChatMinimised}
                onToggleMinimise={() => {
                  const next = !isChatMinimised;
                  setIsChatMinimised(next);
                  if (next) localStorage.setItem('aidey-chat-minimised', 'true');
                  else localStorage.removeItem('aidey-chat-minimised');
                }}
                onClose={() => { if (popupCloseRef.current) popupCloseRef.current(); else setShowAideyChat(false); }}
                onExpand={() => {
                  // The full page opens on this same conversation (AideyChatDashboard reads it once).
                  try { sessionStorage.setItem('aidey-expand-session', popupActiveSessionRef.current || popupSessionId); } catch { /* storage blocked: the page opens on its own choice */ }
                  if (popupCloseRef.current) popupCloseRef.current(); else setShowAideyChat(false);
                  handleMenuItemClick('AideyChat');
                }}
              >
                <SuperAideyChat
                  key={popupSessionId}
                  onCreateTask={handleAddTaskFromChat}
                  userSchedule={tasks}
                  googleEvents={googleEvents}
                  icalEvents={icalEvents}
                  outlookEvents={outlookEvents}
                  onMoveTask={chatTaskActions.moveTask}
                  onCancelTask={chatTaskActions.cancelTask}
                  isPopup={true}
                  onClose={() => setShowAideyChat(false)}
                  closeRef={popupCloseRef}
                  sessionId={popupSessionId}
                  setFilter={setFilter}
                  onCreateMeetingTask={handleMeetingRecorderCreateTask}
                  proactiveMessage={proactiveMessage}
                  onProactiveHandled={() => setProactiveMessage(null)}
                  notificationProactive={notificationProactive}
                  onNotificationProactiveHandled={() => setNotificationProactive(null)}
                  clashContext={clashContext}
                  onClashContextHandled={() => setClashContext(null)}
                  initialInput={prefillChatInput}
                  notifContext={notifContext}
                  onOpenDocumentImporter={(opts) => { setShowAideyChat(false); setDocumentImporterFile(opts?.file || null); setShowDocumentImporter(true); }}
                  sessionRef={popupActiveSessionRef}
                />
              </AideyPopup>
            )}

            {showClashModal && clashInfo && (() => {
              // Pick the best suggested slot to show
              const bestSlot = clashInfo.multiDaySlot && clashInfo.multiDaySlot.dayOffset > 0
                ? clashInfo.multiDaySlot
                : { slotStart: clashInfo.slotStart, slotEnd: clashInfo.slotEnd, dayOffset: 0 };

              const fmtSlot = (s, e) => {
                const sameDay = s.toDateString() === new Date().toDateString();
                const dateLabel = sameDay
                  ? 'today'
                  : s.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
                return `${dateLabel} at ${formatTaskClock(s)}–${formatTaskClock(e)}`;
              };

              const conflictTitle = clashInfo.conflictingTask?.title || clashInfo.conflictingTask?.summary || 'another event';
              const conflictTime = formatTaskTimeRange(clashInfo.conflictingTask) || null;   // fix(task-dates): no end from an empty deadline

              const dismissClash = () => {
                setShowClashModal(false);
                setClashSlotConfirming(false);
                setClashInfo(null);
              };

              return (
                <div style={{
                  position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                  background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center",
                  justifyContent: "center", zIndex: 'var(--z-modal)', padding: "1rem", boxSizing: "border-box",
                }}>
                  <div style={{
                    background: "#fff", color: "#242629", border: "2px solid #f0c040",
                    borderRadius: 14, padding: "2rem", width: "100%", maxWidth: 420,
                    boxShadow: "0 8px 40px rgba(0,0,0,0.18)", position: "relative",
                  }}>
                    <button
                      onClick={dismissClash}
                      style={{ position: "absolute", top: 12, right: 16, background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#999", lineHeight: 1 }}
                      aria-label="Close"
                    >✕</button>

                    {!clashSlotConfirming ? (
                      <>
                        <h3 style={{ margin: "0 0 0.75rem", color: "#D7263D", fontSize: "1.1rem" }}>⚠️ Scheduling Conflict</h3>
                        <p style={{ margin: "0 0 0.5rem", lineHeight: 1.5 }}>
                          <strong>"{clashInfo.title}"</strong> overlaps with <strong>"{conflictTitle}"</strong>
                          {conflictTime && <span style={{ color: "#888", fontSize: "0.88rem" }}> ({conflictTime})</span>}.
                        </p>

                        {clashInfo.swapSuggestion && (
                          <p style={{ fontSize: "0.85rem", background: "#fff8e1", border: "1px solid #f0c040", borderRadius: 8, padding: "0.6rem 0.85rem", color: "#6c4a00", margin: "0.75rem 0" }}>
                            💡 {clashInfo.swapSuggestion.message}
                          </p>
                        )}

                        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginTop: "1.25rem" }}>
                          {/* Continue Anyway */}
                          <button
                            style={{ background: "#47A992", color: "#fff", border: "none", borderRadius: 8, padding: "0.65rem 1rem", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem", textAlign: "left" }}
                            onClick={() => {
                              createAndAddTask(clashInfo.formTask);
                              resetAndCloseTaskModal();
                              dismissClash();
                            }}
                          >
                            ✅ Continue Anyway
                            <span style={{ display: "block", fontWeight: 400, fontSize: "0.78rem", opacity: 0.85, marginTop: 2 }}>Save at the original time, accepting the overlap</span>
                          </button>

                          {/* Book Next Available Slot */}
                          <button
                            style={{ background: "#E4E69B", color: "#242629", border: "none", borderRadius: 8, padding: "0.65rem 1rem", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem", textAlign: "left" }}
                            onClick={() => setClashSlotConfirming(true)}
                          >
                            📅 Book Next Available Slot
                            <span style={{ display: "block", fontWeight: 400, fontSize: "0.78rem", opacity: 0.75, marginTop: 2 }}>
                              {fmtSlot(bestSlot.slotStart, bestSlot.slotEnd)}
                            </span>
                          </button>

                          {/* Move conflicting task (only for Aidey tasks) */}
                          {clashInfo.swapSuggestion && (
                            <button
                              style={{ background: "#fff8e1", color: "#6c4a00", border: "1px solid #f0c040", borderRadius: 8, padding: "0.65rem 1rem", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem", textAlign: "left" }}
                              onClick={() => {
                                const freed = findNextAvailableSlotMultiDay(
                                  tasks.filter(t => t.id !== clashInfo.swapSuggestion.clashTask.id),
                                  new Date(clashInfo.swapSuggestion.clashTask.datetime),
                                  new Date(clashInfo.swapSuggestion.clashTask.deadline)
                                );
                                if (freed) {
                                  const movedTask = { ...clashInfo.swapSuggestion.clashTask, datetime: freed.slotStart, deadline: freed.slotEnd };
                                  setTasks(tasks.map(t => t.id === movedTask.id ? movedTask : t));
                                  api(`/api/tasks/${movedTask.id}`, {
                                    method: 'PUT',
                                    body: JSON.stringify({ datetime: freed.slotStart, deadline: freed.slotEnd, dueChange: { source: 'task_edit' } }),
                                  }).catch(err => console.error('Failed to reschedule task:', err));
                                }
                                createAndAddTask(clashInfo.formTask);
                                resetAndCloseTaskModal();
                                dismissClash();
                              }}
                            >
                              🔀 Move "{clashInfo.swapSuggestion.clashTask.title}" instead
                              <span style={{ display: "block", fontWeight: 400, fontSize: "0.78rem", opacity: 0.75, marginTop: 2 }}>Reschedule the lower-priority task and take this slot</span>
                            </button>
                          )}

                          {/* Ask Aidey */}
                          <button
                            style={{ background: "#FFA384", color: "#242629", border: "none", borderRadius: 8, padding: "0.65rem 1rem", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem", textAlign: "left" }}
                            onClick={() => {
                              const clashDate = new Date(clashInfo.start);
                              const dateStr = clashDate.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
                              const timeStr = formatTaskClock(clashDate);
                              const ctx = `I have a scheduling conflict on ${dateStr} at ${timeStr}, "${clashInfo.title}" overlaps with "${conflictTitle}". Can you help me find a good time to schedule "${clashInfo.title}"?`;
                              setClashContext(ctx);
                              setPopupSessionId(newSessionId());
                              setShowAideyChat(true);
                              dismissClash();
                            }}
                          >
                            🤖 Ask Aidey for help
                            <span style={{ display: "block", fontWeight: 400, fontSize: "0.78rem", opacity: 0.75, marginTop: 2 }}>Aidey will suggest an alternative time</span>
                          </button>

                          {/* Cancel */}
                          <button
                            style={{ background: "none", color: "#888", border: "1px solid #e0e0e0", borderRadius: 8, padding: "0.5rem 1rem", fontWeight: 500, cursor: "pointer", fontSize: "0.9rem" }}
                            onClick={dismissClash}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      /* Book Next Slot — confirmation step */
                      <>
                        <h3 style={{ margin: "0 0 0.75rem", color: "#0C2D48", fontSize: "1.1rem" }}>📅 Confirm New Time</h3>
                        <p style={{ lineHeight: 1.5, margin: "0 0 1rem" }}>
                          Book <strong>"{clashInfo.title}"</strong> for:
                        </p>
                        <div style={{ background: "#f0f7f4", border: "2px solid #47A992", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
                          <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#0C2D48" }}>
                            {bestSlot.slotStart.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })}
                          </div>
                          <div style={{ fontSize: "0.95rem", color: "#333", marginTop: 4 }}>
                            {formatTaskClock(bestSlot.slotStart)} – {formatTaskClock(bestSlot.slotEnd)}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "0.75rem" }}>
                          <button
                            style={{ flex: 1, background: "#47A992", color: "#fff", border: "none", borderRadius: 8, padding: "0.7rem 1rem", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem" }}
                            onClick={() => {
                              createAndAddTask({
                                ...clashInfo.formTask,
                                datetime: bestSlot.slotStart,
                                deadline: bestSlot.slotEnd,
                              });
                              resetAndCloseTaskModal();
                              dismissClash();
                            }}
                          >
                            Confirm
                          </button>
                          <button
                            style={{ background: "#f0f0f0", color: "#555", border: "none", borderRadius: 8, padding: "0.7rem 1.25rem", fontWeight: 600, cursor: "pointer", fontSize: "0.95rem" }}
                            onClick={() => setClashSlotConfirming(false)}
                          >
                            Back
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}


          </main>
          </ErrorBoundary>
          </div>
          </div>
        </div>
      )}

      {/* Team Pro upgrade prompt modal */}
      {showTeamUpgradeModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 'var(--z-modal)', display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowTeamUpgradeModal(false)}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "2.5rem", maxWidth: 460, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", textAlign: "center" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🏢</div>
            <h2 style={{ margin: "0 0 0.75rem", fontSize: "1.4rem", color: "#0C2D48" }}>Upgrade to Team Pro</h2>
            <p style={{ color: "#555", lineHeight: 1.6, marginBottom: "1.5rem" }}>
              Team Pro unlocks shared workspaces, team task boards, team calendar, team chat,
              leave management, and manager visibility.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
              <button
                onClick={() => { setShowTeamUpgradeModal(false); handleUpgradeRequest('team'); }}
                style={{ padding: "0.7rem 1.75rem", background: "#E4E69B", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}
              >
                Upgrade to Team Pro
              </button>
              <button
                onClick={() => setShowTeamUpgradeModal(false)}
                style={{ padding: "0.7rem 1.25rem", background: "#f3f4f6", border: "none", borderRadius: 8, fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", color: "#555" }}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Pro first-login welcome modal */}
      {showTeamWelcomeModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 'var(--z-modal)', display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "2.5rem", maxWidth: 460, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", textAlign: "center" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🎉</div>
            <h2 style={{ margin: "0 0 0.75rem", fontSize: "1.4rem", color: "#0C2D48" }}>Welcome to Aidey Team Pro!</h2>
            <p style={{ color: "#555", lineHeight: 1.6, marginBottom: "1.5rem" }}>
              Start by inviting your team members to collaborate on tasks, share calendars, and manage workloads together.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
              <button
                onClick={() => { dismissTeamWelcome(); handleMenuItemClick("TeamManagement"); }}
                style={{ padding: "0.7rem 1.75rem", background: "#E4E69B", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}
              >
                Invite Team Members
              </button>
              <button
                onClick={dismissTeamWelcome}
                style={{ padding: "0.7rem 1.25rem", background: "#f3f4f6", border: "none", borderRadius: 8, fontWeight: 600, fontSize: "0.95rem", cursor: "pointer", color: "#555" }}
              >
                Explore first
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global search overlay — Cmd+K / Ctrl+K */}
      {showSearch && (
        <SearchOverlay
          notes={quickNotes}
          tasks={tasks}
          /* Pages: exactly the sidebar items this person sees in this workspace (decision 20). */
          pages={sidebarViews({
            isPersonal: isPersonalWorkspace,
            hasTeamPlanAccess,
            userIsTeamAdmin,
            role: currentUser?.role,
            canSeeLedger: !!currentUser?.canSeeLedger,   // decision 70: search offers what the sidebar does
          }).map(v => ({ key: v.key, icon: v.icon, label: v.labelKey ? t(v.labelKey) : v.label }))}
          isPersonal={personalFlag(workspaces, activeWorkspaceId)}
          onClose={() => setShowSearch(false)}
          onNavigate={(section) => { setFilter(section); setShowSearch(false); }}
          onViewTask={(task) => { setViewTask(task); setShowSearch(false); }}
        />
      )}

      {/* Document importer — opened from Aidey chat or Projects header */}
      {showDocumentImporter && (
        <DocumentImporter
          initialFile={documentImporterFile}
          onClose={() => { setShowDocumentImporter(false); setDocumentImporterFile(null); }}
          onViewProject={(projectId) => {
            setShowDocumentImporter(false);
            setFilter('ProjectTracker');
          }}
        />
      )}

      {/* Decision 2: deleting from an occurrence asks whether it is only this one or every one. */}
      <RecurringEditDialog
        action="delete"
        open={!!taskDeleteConfirm?.occurrence}
        onClose={() => setTaskDeleteConfirm(null)}
        onChoose={(scope) => {
          const chosen = taskDeleteConfirm;
          setTaskDeleteConfirm(null);
          if (scope === 'this') deleteOccurrence(chosen.occurrence);
          else handleDelete(chosen.id);
        }}
      />
      <ConfirmDeleteModal
        open={taskDeleteConfirm !== null && !taskDeleteConfirm.occurrence}
        onClose={() => setTaskDeleteConfirm(null)}
        onConfirm={() => handleDelete(taskDeleteConfirm.id)}
        itemType="task"
        itemName={taskDeleteConfirm?.name || ''}
        warningText="This will permanently delete this task."
      />
    </>
  );
}

// The neutral splash while the session and the workspace are being found.
function BootSplash() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(150deg, #0C2D48 0%, #1a4a7a 50%, #0a2038 100%)',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 44, height: 44, border: '4px solid rgba(228,230,155,0.3)',
          borderTopColor: '#E4E69B', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Raleway, sans-serif', fontSize: 14, margin: 0 }}>
          Loading…
        </p>
      </div>
    </div>
  );
}

// Remounts AppContent when activeWorkspaceId changes, clearing all cached data.
//
// Carla, 2026-10-11: the Team page sat about 8 seconds on "Loading…". AppContent was mounted
// UNDER this splash while the session was restored: the moment the user was known its effects
// fired the whole boot set (tasks, notes, invoices, contacts, expenses, notifications...) with no
// workspace chosen, competing with the two requests the splash was waiting for; then the
// workspace arrived, the key changed, and AppContent remounted and fetched it all again (23
// requests, 9 of them twice, measured on the preview). It now mounts once, when the workspace is
// known, or once loading the workspaces has finished without one (an error, or none to choose).
function WorkspaceReloader() {
  const { currentUser, activeWorkspaceId, workspacesSettled, workspacesLoadError } = useCurrentUser();
  if (currentUser && !activeWorkspaceId && !workspacesSettled && !workspacesLoadError) return <BootSplash />;
  return <AppContent key={activeWorkspaceId || 'default'} />;
}

// Main App wrapper that provides context
function App() {
  return (
    <TourProvider>
      <CurrentUserProvider>
        <ContactsProvider>
          <InvoicesProvider>
            <ExpensesProvider>
              <ICalProvider>
                <WorkspaceReloader />
                <PWAInstallPrompt />
              </ICalProvider>
            </ExpensesProvider>
          </InvoicesProvider>
        </ContactsProvider>
      </CurrentUserProvider>
    </TourProvider>
  );
}

export default AppWrapper;
